/**
 * PreVendaPage Component
 * 
 * Página de criação e gerenciamento de pré-vendas.
 * Interface similar a Vendas com busca de produtos, carrinho e finalização.
 * 
 * Funcionalidades:
 * - Busca de produtos
 * - Adição de produtos ao pedido
 * - Seleção de vendedor e tabela de preços
 * - Finalização de pré-venda
 * - NÃO altera estoque ao finalizar (apenas reserva)
 * - NÃO abre modal de pagamento (não há recebimento na pré-venda)
 * - NÃO precisa de caixa aberto
 */

import { useState, useEffect, useCallback, useRef, useMemo } from 'react'
import { 
  ShoppingCart, X, CheckCircle,
  Clock, ChevronDown, Calculator,
  Percent, LogOut, ArrowRight, Loader2,
  Info, Sparkles, CheckCircle2, Trash2, Plus, Minus,
  Search, RefreshCcw, FileHeart, UserRoundPlus, UserRound, Table2, BrainCircuit, WifiOff, MapPin, Package, Printer
} from 'lucide-react'
import { useAuth } from '../../../../contexts/AuthContext'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { usePermissions, MODULES, ACTIONS } from '../../../../hooks/usePermissions'
import ConfirmModal from '../../../../components/ERP/ConfirmModal/ConfirmModal'
import ERPProductSearch from '../../../../components/ERP/ProductSearch/ERPProductSearch'
import { 
  saleService, 
  customerService,
  sellerService,
  specialPricingTablesService,
  quoteService
} from '../../../../services/erpApi'
import { orderService } from '../../../../services/api'
import { isUserAuthorizedSeller } from '../../../../utils/sellerAuthorization'
import SellerAssistantPanel from '../../../../components/SellerAssistant/SellerAssistantPanel'
import {
  isBrowserOnline,
  isNetworkFailure,
  saveActiveSessionDraft,
  enqueuePendingDraft,
  clearActiveLocalDraft,
  getOrCreateActiveDraft,
  listPendingDrafts,
  putSellersCache,
  getSellersCache,
  putCustomersCache,
  putCustomerCache,
  searchCustomersCache,
} from '../../../../services/presaleDraftDb'
import { syncPendingPresaleDrafts } from '../../../../services/presaleOfflineSync'
import PreVendaFreightDropdown from './PreVendaFreightDropdown'
import './PreVendaPage.css'

function isLegalEntityCustomer(customer) {
  if (!customer) return false

  const type = customer.type ?? customer.tipo_pessoa ?? customer.tipoPessoa
  if (type === 'J') return true
  if (type === 'F') return false

  const doc = String(customer.cpf_cnpj ?? customer.cpfCnpj ?? customer.document ?? '').replace(/\D/g, '')
  if (doc.length === 14) return true
  if (doc.length === 11) return false

  return false
}

function canApplyPriceTable(customer) {
  return Boolean(customer?.id && isLegalEntityCustomer(customer))
}

const PRICE_TABLE_CNPJ_HINT =
  'A tabela somente atualiza os preços após a indicação do cliente CNPJ.'

function PreVendaPage() {
  const { user, logout, loading: authLoading } = useAuth()
  const navigate = useNavigate()
  const [searchParams, setSearchParams] = useSearchParams()
  const { hasPermission } = usePermissions()
  const canImportQuote = hasPermission(MODULES.QUOTES, ACTIONS.QUOTES_CONVERT)
  const canImportOnlineOrder = hasPermission(MODULES.SALES, ACTIONS.SALES_PRESALE)
    && hasPermission(MODULES.SALES, ACTIONS.SALES_PRESALE)
  const canAccessSellerAssistant = hasPermission(MODULES.SALES, ACTIONS.SALES_SELLER_ASSISTANT)
  const [sellerAssistantOpen, setSellerAssistantOpen] = useState(() => {
    try {
      return sessionStorage.getItem('seller-assistant-open') === 'true'
    } catch {
      return false
    }
  })
  
  // Refs para dropdowns
  const userMenuRef = useRef(null)
  const sellerAutoSelectedRef = useRef(false)
  const importQuoteHandledRef = useRef(false)
  const skipTableRecalculationRef = useRef(false)

  useEffect(() => {
    try {
      sessionStorage.setItem('seller-assistant-open', String(sellerAssistantOpen))
    } catch {
      /* ignore */
    }
  }, [sellerAssistantOpen])
  
  // Estados do pedido
  const [selectedProducts, setSelectedProducts] = useState([])
  const [selectedSeller, setSelectedSeller] = useState(null)
  const [selectedSellerId, setSelectedSellerId] = useState(null)
  const [sellers, setSellers] = useState([])
  const [selectedPriceTable, setSelectedPriceTable] = useState(null)
  const [selectedPriceTableName, setSelectedPriceTableName] = useState(null)
  const [selectedCustomer, setSelectedCustomer] = useState(null)
  const [observations, setObservations] = useState('')
  const [loading, setLoading] = useState(false)
  const [customerDropdownOpen, setCustomerDropdownOpen] = useState(false)
  const [sellerModalOpen, setSellerModalOpen] = useState(false)
  const [sellerSearchTerm, setSellerSearchTerm] = useState('')
  const [priceTableModalOpen, setPriceTableModalOpen] = useState(false)
  const [priceTableSearchTerm, setPriceTableSearchTerm] = useState('')
  const [priceTables, setPriceTables] = useState([])
  const [loadingPriceTables, setLoadingPriceTables] = useState(false)
  const [customers, setCustomers] = useState([])
  const [customerSearchTerm, setCustomerSearchTerm] = useState('')
  const [loadingCustomers, setLoadingCustomers] = useState(false)
  const [quickCreateModalOpen, setQuickCreateModalOpen] = useState(false)
  const [quickCreateForm, setQuickCreateForm] = useState({
    name: '',
    cpfCnpj: '',
    email: '',
    phone: ''
  })
  const [creatingCustomer, setCreatingCustomer] = useState(false)
  const [selectedProductForAction, setSelectedProductForAction] = useState(null)
  const [quantityModalOpen, setQuantityModalOpen] = useState(false)
  const [discountModalOpen, setDiscountModalOpen] = useState(false)
  const [surchargeModalOpen, setSurchargeModalOpen] = useState(false)
  const [waitingPresalesModalOpen, setWaitingPresalesModalOpen] = useState(false)
  const [waitingPresales, setWaitingPresales] = useState([])
  const [loadingWaitingPresales, setLoadingWaitingPresales] = useState(false)
  const [showUserModal, setShowUserModal] = useState(false)
  const [showHistoryModal, setShowHistoryModal] = useState(false)
  const [isSaving, setIsSaving] = useState(false)
  const [importQuoteModalOpen, setImportQuoteModalOpen] = useState(false)
  const [approvedQuotes, setApprovedQuotes] = useState([])
  const [quoteSearchTerm, setQuoteSearchTerm] = useState('')
  const [loadingApprovedQuotes, setLoadingApprovedQuotes] = useState(false)
  const [importedSaleId, setImportedSaleId] = useState(null)
  const [importedQuoteId, setImportedQuoteId] = useState(null)
  const [freightOpen, setFreightOpen] = useState(false)
  const [freightQuote, setFreightQuote] = useState(null)
  const freightBtnRef = useRef(null)
  const [importedQuoteNumber, setImportedQuoteNumber] = useState(null)
  const [importOnlineOrderModalOpen, setImportOnlineOrderModalOpen] = useState(false)
  const [onlineOrdersForPresale, setOnlineOrdersForPresale] = useState([])
  const [onlineOrderSearchTerm, setOnlineOrderSearchTerm] = useState('')
  const [loadingOnlineOrders, setLoadingOnlineOrders] = useState(false)
  const [importedOnlineOrderId, setImportedOnlineOrderId] = useState(null)
  const [importedOnlineOrderNumber, setImportedOnlineOrderNumber] = useState(null)
  const [importedOnlineOrderPaid, setImportedOnlineOrderPaid] = useState(false)
  const [isOffline, setIsOffline] = useState(() => !isBrowserOnline())
  const [pendingSyncCount, setPendingSyncCount] = useState(0)
  const [isSyncingPending, setIsSyncingPending] = useState(false)
  const [draftRestored, setDraftRestored] = useState(false)
  const [draftReady, setDraftReady] = useState(false)
  const draftHydratedRef = useRef(false)
  const prevOfflineRef = useRef(!isBrowserOnline())
  const initialPendingSyncRef = useRef(false)
  /** Evita retry imediato quando a API está fora (navigator.onLine ainda true). */
  const skipAutoSyncUntilReconnectRef = useRef(false)
  
  // Estados do modal de confirmação
  const [confirmModal, setConfirmModal] = useState({
    isOpen: false,
    title: '',
    message: '',
    type: 'info',
    onConfirm: null,
    confirmText: 'Confirmar',
    cancelText: 'Cancelar',
    showCancel: true
  })

  // Calcular preço total do produto considerando desconto e acréscimo
  const calculateProductTotal = useCallback((product) => {
    let baseTotal = product.quantity * product.unitPrice
    
    // Aplicar desconto (sempre calculado sobre o total)
    let discountAmount = 0
    if (product.discount && product.discount > 0) {
      if (product.discountType === 'percent') {
        discountAmount = baseTotal * (product.discount / 100)
      } else {
        discountAmount = product.discount
      }
    }
    
    // Aplicar acréscimo (sempre calculado sobre o total)
    let surchargeAmount = 0
    if (product.surcharge && product.surcharge > 0) {
      if (product.surchargeType === 'percent') {
        surchargeAmount = baseTotal * (product.surcharge / 100)
      } else {
        surchargeAmount = product.surcharge
      }
    }
    
    return {
      baseTotal,
      discountAmount,
      surchargeAmount,
      finalTotal: baseTotal - discountAmount + surchargeAmount
    }
  }, [])

  // Buscar preço do produto considerando tabela de preços (somente com cliente PJ)
  const getProductPrice = useCallback(async (product) => {
    let productPrice = product.price ? parseFloat(product.price) : 0

    if (selectedPriceTable && canApplyPriceTable(selectedCustomer)) {
      try {
        const priceResponse = await specialPricingTablesService.calculatePrice(
          parseInt(selectedCustomer.id, 10),
          product.id,
          Number(selectedPriceTable)
        )
        if (priceResponse.success && priceResponse.data?.price !== null && priceResponse.data?.price !== undefined) {
          productPrice = parseFloat(priceResponse.data.price)
        }
      } catch (error) {
        console.error('Erro ao buscar preço da tabela:', error)
      }
    }

    return productPrice
  }, [selectedPriceTable, selectedCustomer])

  // Adicionar produto diretamente do ERPProductSearch
  const handleAddProduct = useCallback(async (product) => {
    const productPrice = await getProductPrice(product)
    const originalPrice = product.price ? parseFloat(product.price) : 0
    
    setSelectedProducts(prev => {
      const existingIndex = prev.findIndex(p => p.id === product.id)
      
      if (existingIndex >= 0) {
        if (prev[existingIndex].fromQuote) {
          return prev
        }
        // Atualizar quantidade e preço; item volta ao topo da fila
        const updatedItem = {
          ...prev[existingIndex],
          quantity: prev[existingIndex].quantity + 1,
          unitPrice: productPrice
        }
        const calc = calculateProductTotal(updatedItem)
        updatedItem.totalPrice = calc.finalTotal
        const rest = prev.filter((_, i) => i !== existingIndex)
        return [updatedItem, ...rest]
      }

      // Novo produto no topo da fila (mais recente primeiro)
      return [
        {
          ...product,
          quantity: 1,
          unitPrice: productPrice,
          originalPrice: originalPrice,
          discount: 0,
          discountType: 'percent',
          surcharge: 0,
          surchargeType: 'percent',
          totalPrice: productPrice
        },
        ...prev
      ]
    })
  }, [calculateProductTotal, getProductPrice])

  // Calcular totais usando useMemo
  const subtotal = useMemo(() => {
    return selectedProducts.reduce((acc, item) => {
      const calc = calculateProductTotal(item)
      return acc + calc.baseTotal
    }, 0)
  }, [selectedProducts, calculateProductTotal])

  const totalDiscountValue = useMemo(() => {
    return selectedProducts.reduce((acc, item) => {
      const calc = calculateProductTotal(item)
      return acc + calc.discountAmount
    }, 0)
  }, [selectedProducts, calculateProductTotal])

  const totalSurchargeValue = useMemo(() => {
    return selectedProducts.reduce((acc, item) => {
      const calc = calculateProductTotal(item)
      return acc + calc.surchargeAmount
    }, 0)
  }, [selectedProducts, calculateProductTotal])

  const finalTotal = useMemo(() => {
    return subtotal - totalDiscountValue + totalSurchargeValue
  }, [subtotal, totalDiscountValue, totalSurchargeValue])

  const totalItemsCount = useMemo(() => {
    return selectedProducts.reduce((acc, item) => acc + item.quantity, 0)
  }, [selectedProducts])

  const userIdKey = String(user?.id || 'anon')

  const refreshPendingCount = useCallback(async () => {
    try {
      const pending = await listPendingDrafts(userIdKey)
      setPendingSyncCount(pending.length)
    } catch {
      /* ignore */
    }
  }, [userIdKey])

  useEffect(() => {
    const onOnline = () => setIsOffline(false)
    const onOffline = () => setIsOffline(true)
    window.addEventListener('online', onOnline)
    window.addEventListener('offline', onOffline)
    setIsOffline(!isBrowserOnline())
    return () => {
      window.removeEventListener('online', onOnline)
      window.removeEventListener('offline', onOffline)
    }
  }, [])

  useEffect(() => {
    if (!user?.id) return
    refreshPendingCount()
  }, [user?.id, refreshPendingCount])

  // Restaurar rascunho local ao abrir
  useEffect(() => {
    if (authLoading) return

    if (!user?.id) {
      setDraftReady(true)
      return
    }

    if (draftHydratedRef.current) return
    draftHydratedRef.current = true

    ;(async () => {
      try {
        const draft = await getOrCreateActiveDraft(userIdKey)
        if (draft.syncStatus === 'local' && draft.items?.length > 0) {
          setSelectedProducts(draft.items)
          setObservations(draft.observations || '')
          setSelectedSellerId(draft.sellerId || null)
          setSelectedSeller(draft.sellerName || null)
          setSelectedPriceTable(draft.priceTableId || null)
          setSelectedPriceTableName(draft.priceTableName || null)
          setImportedSaleId(draft.importedSaleId || null)
          setImportedQuoteId(draft.importedQuoteId || null)
          setImportedQuoteNumber(draft.importedQuoteNumber || null)
          setImportedOnlineOrderId(draft.importedOnlineOrderId || null)
          setImportedOnlineOrderNumber(draft.importedOnlineOrderNumber || null)
          setImportedOnlineOrderPaid(Boolean(draft.importedOnlineOrderPaid))
          if (draft.customerSnapshot) {
            setSelectedCustomer(draft.customerSnapshot)
          }
          setDraftRestored(true)
        }
        await refreshPendingCount()
      } catch (err) {
        console.warn('[PreVenda] Falha ao restaurar rascunho:', err)
      } finally {
        setDraftReady(true)
      }
    })()
  }, [authLoading, user?.id, userIdKey, refreshPendingCount])

  // Autosave do rascunho ativo (só após hidratar/restaurar)
  useEffect(() => {
    if (!user?.id || !draftReady) return

    const timer = setTimeout(() => {
      saveActiveSessionDraft(userIdKey, {
        items: selectedProducts,
        sellerId: selectedSellerId,
        sellerName: selectedSeller,
        customerId: selectedCustomer?.id || null,
        customerSnapshot: selectedCustomer || null,
        priceTableId: selectedPriceTable,
        priceTableName: selectedPriceTableName,
        observations,
        importedSaleId,
        importedQuoteId,
        importedQuoteNumber,
        importedOnlineOrderId,
        importedOnlineOrderNumber,
        importedOnlineOrderPaid,
        totals: {
          subtotal,
          discount: totalDiscountValue,
          surcharge: totalSurchargeValue,
          final: finalTotal,
        },
      }).catch((err) => console.warn('[PreVenda] Autosave falhou:', err))
    }, 400)

    return () => clearTimeout(timer)
  }, [
    user?.id,
    userIdKey,
    draftReady,
    selectedProducts,
    selectedSellerId,
    selectedSeller,
    selectedCustomer,
    selectedPriceTable,
    selectedPriceTableName,
    observations,
    importedSaleId,
    importedQuoteId,
    importedQuoteNumber,
    importedOnlineOrderId,
    importedOnlineOrderNumber,
    importedOnlineOrderPaid,
    subtotal,
    totalDiscountValue,
    totalSurchargeValue,
    finalTotal,
  ])

  const filteredSellers = useMemo(() => {
    const term = sellerSearchTerm.trim().toLowerCase()
    if (!term) return sellers
    return sellers.filter(seller =>
      seller.name?.toLowerCase().includes(term) ||
      seller.email?.toLowerCase().includes(term)
    )
  }, [sellers, sellerSearchTerm])

  // Buscar vendedores (API + cache IndexedDB) e pré-selecionar o usuário logado quando autorizado
  useEffect(() => {
    if (authLoading || !draftReady) return

    let cancelled = false

    const applySellerList = (sellersList) => {
      let list = sellersList || []

      if (user && isUserAuthorizedSeller(user)) {
        const alreadyInList = list.some(s => s.id === user.id)
        if (!alreadyInList) {
          list = [
            {
              id: user.id,
              name: user.name,
              email: user.email,
              role: user.role,
              is_seller: user.is_seller,
              avatar_url: user.avatar_url
            },
            ...list
          ]
        }

        if (!sellerAutoSelectedRef.current) {
          sellerAutoSelectedRef.current = true
          setSelectedSellerId((prevId) => {
            if (prevId) return prevId
            const match = list.find(s => s.id === user.id)
            setSelectedSeller(match?.name || user.name)
            return user.id
          })
        }
      }

      setSellers(list)
    }

    const loadSellers = async () => {
      try {
        if (!isBrowserOnline()) {
          const cached = await getSellersCache()
          if (!cancelled) applySellerList(cached)
          return
        }

        const response = await sellerService.getAll()
        if (cancelled) return

        if (!response.success) {
          const cached = await getSellersCache()
          if (cached.length) applySellerList(cached)
          return
        }

        const sellersList = response.data || []
        await putSellersCache(sellersList)
        if (!cancelled) applySellerList(sellersList)
      } catch (error) {
        console.error('Erro ao buscar vendedores:', error)
        try {
          const cached = await getSellersCache()
          if (!cancelled) applySellerList(cached)
        } catch {
          if (!cancelled) setSellers([])
        }
      }
    }

    loadSellers()
    return () => {
      cancelled = true
    }
  }, [authLoading, user, draftReady])

  const selectedProductsRef = useRef(selectedProducts)
  selectedProductsRef.current = selectedProducts

  const recalculatePricesFromTable = useCallback(async () => {
    const tableId = selectedPriceTable ? Number(selectedPriceTable) : null
    const currentProducts = selectedProductsRef.current

    if (!tableId || currentProducts.length === 0 || !canApplyPriceTable(selectedCustomer)) {
      return
    }

    const clientId = parseInt(selectedCustomer.id, 10)

    try {
      const updatedProducts = await Promise.all(
        currentProducts.map(async (product) => {
          try {
            const priceResponse = await specialPricingTablesService.calculatePrice(
              clientId,
              product.id,
              tableId
            )
            if (priceResponse.success && priceResponse.data?.price !== null && priceResponse.data?.price !== undefined) {
              const newPrice = parseFloat(priceResponse.data.price)
              const updated = {
                ...product,
                unitPrice: newPrice,
                discount: product.discountType === 'percent' ? 0 : product.discount,
                discountType: product.discountType === 'percent' ? 'percent' : product.discountType
              }
              const calc = calculateProductTotal(updated)
              updated.totalPrice = calc.finalTotal
              return updated
            }
          } catch (error) {
            console.error(`Erro ao buscar preço para produto ${product.id}:`, error)
          }
          return product
        })
      )
      setSelectedProducts(updatedProducts)
    } catch (error) {
      console.error('Erro ao atualizar preços:', error)
    }
  }, [selectedPriceTable, selectedCustomer, calculateProductTotal])

  // Restaurar preços originais ao remover a tabela
  useEffect(() => {
    if (selectedPriceTable || selectedProductsRef.current.length === 0) {
      return
    }

    setSelectedProducts(currentProducts =>
      currentProducts.map(product => {
        if (product.originalPrice !== undefined && product.originalPrice !== null) {
          const updated = { ...product, unitPrice: product.originalPrice }
          const calc = calculateProductTotal(updated)
          updated.totalPrice = calc.finalTotal
          return updated
        }
        return product
      })
    )
  }, [selectedPriceTable, calculateProductTotal])

  // Remover tabela se o cliente for removido ou não for pessoa jurídica
  useEffect(() => {
    if (!selectedPriceTable) {
      return
    }
    if (!canApplyPriceTable(selectedCustomer)) {
      setSelectedPriceTable(null)
      setSelectedPriceTableName(null)
    }
  }, [selectedCustomer, selectedPriceTable])

  // Recalcular preços quando tabela ou cliente PJ mudarem
  useEffect(() => {
    if (skipTableRecalculationRef.current) {
      skipTableRecalculationRef.current = false
      return
    }

    if (!selectedPriceTable || selectedProducts.length === 0 || !canApplyPriceTable(selectedCustomer)) {
      return
    }

    const timeoutId = setTimeout(() => {
      recalculatePricesFromTable()
    }, 300)

    return () => clearTimeout(timeoutId)
  }, [selectedPriceTable, selectedCustomer, selectedProducts.length, recalculatePricesFromTable])

  // Buscar tabelas de preços reais do sistema quando o modal abrir ou termo de busca mudar
  useEffect(() => {
    if (!priceTableModalOpen) {
      return
    }

    const loadPriceTables = async () => {
      setLoadingPriceTables(true)
      try {
        const filters = {
          active: true,
          limit: 50
        }
        
        if (priceTableSearchTerm.trim()) {
          filters.search = priceTableSearchTerm.trim()
        }

        const response = await specialPricingTablesService.getAll(filters)
        
        if (response.success) {
          setPriceTables(response.data || [])
        } else {
          setPriceTables([])
        }
      } catch (error) {
        console.error('Erro ao buscar tabelas de preços:', error)
        setPriceTables([])
      } finally {
        setLoadingPriceTables(false)
      }
    }

    // Debounce: aguardar 300ms após o usuário parar de digitar
    const timeoutId = setTimeout(() => {
      loadPriceTables()
    }, priceTableSearchTerm.trim() ? 300 : 0)

    return () => clearTimeout(timeoutId)
  }, [priceTableModalOpen, priceTableSearchTerm])

  // Buscar clientes quando o modal abrir ou termo de busca mudar (cache offline)
  useEffect(() => {
    if (!customerDropdownOpen) {
      return
    }

    const loadFromCache = async (term) => {
      const cached = await searchCustomersCache(term)
      setCustomers(cached)
    }

    if (!customerSearchTerm.trim()) {
      const loadAllCustomers = async () => {
        setLoadingCustomers(true)
        try {
          if (!isBrowserOnline()) {
            await loadFromCache('')
            return
          }
          const response = await customerService.getAll({})
          if (response.success) {
            const list = response.data || []
            setCustomers(list)
            await putCustomersCache(list.slice(0, 100))
          } else {
            await loadFromCache('')
          }
        } catch (error) {
          console.error('Erro ao buscar clientes:', error)
          await loadFromCache('')
        } finally {
          setLoadingCustomers(false)
        }
      }
      loadAllCustomers()
      return
    }

    const timeoutId = setTimeout(async () => {
      setLoadingCustomers(true)
      try {
        if (!isBrowserOnline()) {
          await loadFromCache(customerSearchTerm.trim())
          return
        }
        const filters = { search: customerSearchTerm.trim() }
        const response = await customerService.getAll(filters)

        if (response.success) {
          const list = response.data || []
          setCustomers(list)
          await putCustomersCache(list)
        } else {
          await loadFromCache(customerSearchTerm.trim())
        }
      } catch (error) {
        console.error('Erro ao buscar clientes:', error)
        await loadFromCache(customerSearchTerm.trim())
      } finally {
        setLoadingCustomers(false)
      }
    }, 300)

    return () => clearTimeout(timeoutId)
  }, [customerDropdownOpen, customerSearchTerm])

  // Fechar modal com ESC
  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape') {
        if (customerDropdownOpen) {
          setCustomerDropdownOpen(false)
        }
        if (sellerModalOpen) {
          setSellerModalOpen(false)
        }
        if (priceTableModalOpen) {
          setPriceTableModalOpen(false)
        }
        if (importQuoteModalOpen) {
          setImportQuoteModalOpen(false)
        }
      }
    }

    if (customerDropdownOpen || sellerModalOpen || priceTableModalOpen || importQuoteModalOpen) {
      document.addEventListener('keydown', handleEscape)
      document.body.style.overflow = 'hidden'
    }

    return () => {
      document.removeEventListener('keydown', handleEscape)
      document.body.style.overflow = 'unset'
    }
  }, [customerDropdownOpen, sellerModalOpen, priceTableModalOpen, importQuoteModalOpen])

  // Remover produto do pedido (itens importados de orçamento não podem ser removidos)
  const handleRemoveProduct = (productId) => {
    const product = selectedProducts.find(p => p.id === productId)
    if (product?.fromQuote) {
      showMessage(
        'Ação não permitida',
        'Itens importados de um orçamento aprovado não podem ser removidos nesta pré-venda.',
        'warning'
      )
      return
    }
    setSelectedProducts(prev => prev.filter(p => p.id !== productId))
  }

  // Atualizar quantidade do produto
  const handleUpdateProductQuantity = (productId, newQuantity) => {
    if (newQuantity < 1) return
    const product = selectedProducts.find(p => p.id === productId)
    if (product?.fromQuote) return
    setSelectedProducts(prev => prev.map(p => {
      if (p.id === productId) {
        const updated = { ...p, quantity: newQuantity }
        const calc = calculateProductTotal(updated)
        updated.totalPrice = calc.finalTotal
        return updated
      }
      return p
    }))
  }

  // Atualizar item no carrinho (quantidade ou desconto)
  const handleUpdateItem = useCallback((index, field, value) => {
    setSelectedProducts(prev => {
      const current = prev[index]
      if (field === 'quantity' && current?.fromQuote) {
        return prev
      }
      return prev.map((item, i) => {
      if (i === index) {
        const newItem = { ...item }
        if (field === 'quantity') {
          newItem.quantity = Math.max(1, value)
        } else if (field === 'discountPercent') {
          newItem.discount = Math.min(100, Math.max(0, value))
          newItem.discountType = 'percent'
        }
        const calc = calculateProductTotal(newItem)
        newItem.totalPrice = calc.finalTotal
        return newItem
      }
      return item
    })
    })
  }, [calculateProductTotal])

  // Atualizar desconto do produto
  const handleUpdateProductDiscount = (productId, discount, discountType) => {
    setSelectedProducts(selectedProducts.map(p => {
      if (p.id === productId) {
        const updated = {
          ...p,
          discount: discount || 0,
          discountType: discountType || 'percent'
        }
        const calc = calculateProductTotal(updated)
        updated.totalPrice = calc.finalTotal
        return updated
      }
      return p
    }))
  }

  // Atualizar acréscimo do produto
  const handleUpdateProductSurcharge = (productId, surcharge, surchargeType) => {
    setSelectedProducts(selectedProducts.map(p => {
      if (p.id === productId) {
        const updated = {
          ...p,
          surcharge: surcharge || 0,
          surchargeType: surchargeType || 'percent'
        }
        const calc = calculateProductTotal(updated)
        updated.totalPrice = calc.finalTotal
        return updated
      }
      return p
    }))
  }


  // Formatar moeda
  const formatCurrency = (value) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL'
    }).format(value)
  }


  // Helper para mostrar mensagens
  const showMessage = useCallback((title, message, type = 'info', onConfirm = null) => {
    setConfirmModal({
      isOpen: true,
      title,
      message,
      type,
      onConfirm: () => {
        setConfirmModal(prev => ({ ...prev, isOpen: false }))
        if (onConfirm) onConfirm()
      },
      confirmText: 'OK',
      cancelText: 'Cancelar',
      showCancel: false
    })
  }, [])

  const openPriceTableModal = useCallback(() => {
    if (!selectedCustomer?.id) {
      showMessage(
        'Atenção',
        `Selecione um cliente pessoa jurídica antes de escolher a tabela de preços. ${PRICE_TABLE_CNPJ_HINT}`,
        'warning'
      )
      return
    }
    if (!isLegalEntityCustomer(selectedCustomer)) {
      showMessage(
        'Atenção',
        `Tabelas de preços especiais são permitidas apenas para clientes pessoa jurídica. ${PRICE_TABLE_CNPJ_HINT}`,
        'warning'
      )
      return
    }
    setPriceTableModalOpen(true)
  }, [selectedCustomer, showMessage])

  // Helper para confirmações
  const showConfirm = (title, message, type = 'warning', onConfirm) => {
    setConfirmModal({
      isOpen: true,
      title,
      message,
      type,
      onConfirm: () => {
        setConfirmModal(prev => ({ ...prev, isOpen: false }))
        if (onConfirm) onConfirm()
      },
      confirmText: 'Confirmar',
      cancelText: 'Cancelar',
      showCancel: true
    })
  }

  const mapSaleItemsToProducts = useCallback((items) => {
    return (items || []).map(item => ({
      id: item.product_id,
      name: item.product_name || `Produto #${item.product_id}`,
      sku: item.sku || '',
      barcode: item.barcode || '',
      image_url: item.image_url || null,
      price: parseFloat(item.unit_price || 0),
      quantity: parseFloat(item.quantity || 1),
      unitPrice: parseFloat(item.unit_price || 0),
      totalPrice: parseFloat(item.total_price || 0),
      discount: item.discount_percent || item.discount || 0,
      discountType: item.discount_type || 'percent',
      surcharge: item.surcharge || 0,
      surchargeType: item.surcharge_type || 'percent'
    }))
  }, [])

  /** Importa itens do orçamento com preço líquido (desconto já embutido no unitário). */
  const mapQuoteItemsToProducts = useCallback((items) => {
    return (items || []).map(item => {
      const quantity = parseFloat(item.quantity || 1) || 1
      const totalPrice = parseFloat(item.total_price ?? 0)
      const unitPrice = totalPrice > 0
        ? totalPrice / quantity
        : parseFloat(item.unit_price || 0)

      return {
        id: item.product_id,
        name: item.product_name || `Produto #${item.product_id}`,
        sku: item.sku || '',
        price: unitPrice,
        quantity,
        unitPrice,
        totalPrice: totalPrice > 0 ? totalPrice : unitPrice * quantity,
        originalPrice: parseFloat(item.unit_price || unitPrice),
        discount: 0,
        discountType: 'percent',
        surcharge: 0,
        surchargeType: 'percent',
        fromQuote: true
      }
    })
  }, [])

  const loadSaleIntoForm = useCallback(async (saleData) => {
    const items = saleData.items || []

    if (items.length === 0) {
      showMessage(
        'Atenção',
        `A pré-venda #${saleData.number} não possui itens cadastrados.`,
        'warning'
      )
      return false
    }

    setSelectedProducts(mapSaleItemsToProducts(items))

    if (saleData.customer_id) {
      try {
        const customerResponse = await customerService.getById(saleData.customer_id)
        if (customerResponse.success && customerResponse.data) {
          setSelectedCustomer(customerResponse.data)
        }
      } catch (error) {
        console.warn('Erro ao carregar cliente da pré-venda:', error)
      }
    } else {
      setSelectedCustomer(null)
    }

    if (saleData.seller_id) {
      try {
        const sellerResponse = await sellerService.getById(saleData.seller_id)
        if (sellerResponse.success && sellerResponse.data) {
          setSelectedSeller(sellerResponse.data.name)
          setSelectedSellerId(sellerResponse.data.id)
        }
      } catch (error) {
        console.warn('Erro ao carregar vendedor da pré-venda:', error)
      }
    }

    if (saleData.observation) {
      setObservations(saleData.observation)
    }

    return true
  }, [mapSaleItemsToProducts, showMessage])

  const loadQuoteIntoForm = useCallback(async (quoteSummary) => {
    let quoteData = quoteSummary

    if (!quoteData?.items?.length) {
      const quoteResponse = await quoteService.getById(quoteSummary.id)
      if (!quoteResponse.success || !quoteResponse.data) {
        throw new Error('Orçamento não encontrado')
      }
      quoteData = quoteResponse.data
    }

    if (quoteData.status !== 'approved') {
      throw new Error('Apenas orçamentos aprovados podem ser importados para pré-venda')
    }

    const items = quoteData.items || []
    if (items.length === 0) {
      showMessage(
        'Atenção',
        `O orçamento #${quoteData.number || quoteData.id} não possui itens.`,
        'warning'
      )
      return false
    }

    setSelectedProducts(mapQuoteItemsToProducts(items))

    if (quoteData.customer_id) {
      try {
        const customerResponse = await customerService.getById(quoteData.customer_id)
        if (customerResponse.success && customerResponse.data) {
          setSelectedCustomer(customerResponse.data)
        }
      } catch (error) {
        console.warn('Erro ao carregar cliente do orçamento:', error)
      }
    } else {
      setSelectedCustomer(null)
    }

    if (quoteData.seller_id) {
      try {
        const sellerResponse = await sellerService.getById(quoteData.seller_id)
        if (sellerResponse.success && sellerResponse.data) {
          setSelectedSeller(sellerResponse.data.name)
          setSelectedSellerId(sellerResponse.data.id)
        }
      } catch (error) {
        console.warn('Erro ao carregar vendedor do orçamento:', error)
      }
    }

    if (quoteData.pricing_table_id) {
      skipTableRecalculationRef.current = true
      setSelectedPriceTable(quoteData.pricing_table_id)
      setSelectedPriceTableName(quoteData.pricing_table_name || 'Tabela de preços')
    } else {
      setSelectedPriceTable(null)
      setSelectedPriceTableName(null)
    }

    const quoteLabel = quoteData.number || quoteData.id
    const notes = quoteData.client_notes?.trim()
    setObservations(
      notes
        ? `Orçamento #${quoteLabel} - ${notes}`
        : `Orçamento #${quoteLabel}`
    )

    setImportedQuoteId(quoteData.id)
    setImportedQuoteNumber(quoteLabel)
    setImportedSaleId(null)

    return true
  }, [mapQuoteItemsToProducts, showMessage])

  const loadApprovedQuotes = useCallback(async (search = '') => {
    setLoadingApprovedQuotes(true)
    try {
      const response = await quoteService.getApproved(search)
      if (response.success) {
        setApprovedQuotes(response.data || [])
      }
    } catch (error) {
      console.error('Erro ao carregar orçamentos aprovados:', error)
      showMessage('Erro', 'Erro ao carregar orçamentos aprovados', 'danger')
    } finally {
      setLoadingApprovedQuotes(false)
    }
  }, [showMessage])

  const handleImportQuote = useCallback(async (quote) => {
    setImportQuoteModalOpen(false)
    setLoading(true)
    try {
      const loaded = await loadQuoteIntoForm(quote)
      if (loaded) {
        showMessage(
          'Orçamento carregado',
          `Orçamento #${quote.number || quote.id} carregado na sessão.\n\nO orçamento só será convertido ao clicar em Finalizar. Limpar ou atualizar a página mantém o orçamento disponível.`,
          'success'
        )
      }
    } catch (error) {
      console.error('Erro ao importar orçamento:', error)
      showMessage(
        'Erro',
        error.response?.data?.message || error.message || 'Erro ao importar orçamento. Tente novamente.',
        'danger'
      )
    } finally {
      setLoading(false)
    }
  }, [loadQuoteIntoForm, showMessage])

  const handleImportQuoteById = useCallback(async (quoteId) => {
    setLoading(true)
    try {
      const quoteResponse = await quoteService.getById(quoteId)
      if (!quoteResponse.success || !quoteResponse.data) {
        throw new Error('Orçamento não encontrado')
      }

      const quote = quoteResponse.data
      if (quote.status !== 'approved') {
        throw new Error('Apenas orçamentos aprovados podem ser importados para pré-venda')
      }

      await handleImportQuote(quote)
    } catch (error) {
      console.error('Erro ao importar orçamento por ID:', error)
      showMessage(
        'Erro',
        error.response?.data?.message || error.message || 'Erro ao importar orçamento.',
        'danger'
      )
      setLoading(false)
    }
  }, [handleImportQuote, showMessage])

  const mapOnlineOrderItemsToProducts = useCallback((items) => {
    return (items || [])
      .filter((item) => item.product_id)
      .map((item) => {
        const qty = Number(item.quantity) || 1
        const unit = Number(item.unit_price) || 0
        const total = Number(item.total_price) || unit * qty
        return {
          id: item.product_id,
          name: item.product_name || item.product_name_from_db || 'Produto',
          sku: item.product_sku || item.product_sku_from_db || '',
          brand: '',
          image_url: item.product_image_url || item.product_image_url_from_db || null,
          unitPrice: unit,
          quantity: qty,
          discount: 0,
          surcharge: 0,
          totalPrice: total,
          fromOnlineOrder: true
        }
      })
  }, [])

  const loadOnlineOrdersForPresale = useCallback(async (search = '', options = {}) => {
    setLoadingOnlineOrders(true)
    try {
      const response = await orderService.getForPresale(search)
      if (response.success) {
        setOnlineOrdersForPresale(response.data || [])
      }
    } catch (error) {
      console.error('Erro ao carregar pedidos online:', error)
      if (!options.quiet) {
        showMessage('Erro', 'Erro ao carregar pedidos aguardando pré-venda', 'danger')
      }
    } finally {
      setLoadingOnlineOrders(false)
    }
  }, [showMessage])

  useEffect(() => {
    if (!canImportOnlineOrder) return
    loadOnlineOrdersForPresale('', { quiet: true })
  }, [canImportOnlineOrder, loadOnlineOrdersForPresale])

  const loadOnlineOrderIntoForm = useCallback(async (orderSummary) => {
    const detailRes = await orderService.getOrderById(orderSummary.id)
    if (!detailRes.success || !detailRes.data) {
      throw new Error('Pedido não encontrado')
    }
    const order = detailRes.data
    if (order.status !== 'InSeparation') {
      throw new Error('Apenas pedidos Aguardando na pré-venda podem ser importados')
    }
    if (order.sale_id) {
      throw new Error('Pedido já convertido em pré-venda')
    }

    const products = mapOnlineOrderItemsToProducts(order.items)
    if (products.length === 0) {
      throw new Error('Pedido sem itens vinculados a produtos cadastrados')
    }

    setSelectedProducts(products)
    setImportedQuoteId(null)
    setImportedQuoteNumber(null)
    setImportedSaleId(null)
    setImportedOnlineOrderId(order.id)
    setImportedOnlineOrderNumber(order.order_number)
    setImportedOnlineOrderPaid(Boolean(orderSummary.paid_online) || order.payment_timing === 'online')

    // Cliente: tenta achar por CPF/email
    let customer = null
    const cpf = String(order.customer_cpf_cnpj || '').replace(/\D/g, '')
    try {
      const searchTerm = cpf || order.customer_email || order.customer_name
      if (searchTerm) {
        const listRes = await customerService.getAll({ search: searchTerm, limit: 5 })
        const rows = listRes.data || listRes.customers || []
        customer = rows.find((c) => {
          const cCpf = String(c.cpf_cnpj || '').replace(/\D/g, '')
          return (cpf && cCpf === cpf) ||
            (order.customer_email && String(c.email || '').toLowerCase() === String(order.customer_email).toLowerCase())
        }) || rows[0] || null
      }
    } catch (err) {
      console.warn('Busca de cliente para pedido online:', err)
    }

    if (customer) {
      setSelectedCustomer(customer)
    } else {
      setSelectedCustomer({
        id: null,
        name: order.customer_name,
        email: order.customer_email,
        phone: order.customer_phone,
        cpf_cnpj: order.customer_cpf_cnpj,
        _fromOnlineOrder: true
      })
    }

    const ship = [
      order.shipping_street,
      order.shipping_number && `nº ${order.shipping_number}`,
      order.shipping_locality,
      order.shipping_city && `${order.shipping_city}/${order.shipping_region_code || ''}`,
      order.shipping_postal_code && `CEP ${order.shipping_postal_code}`
    ].filter(Boolean).join(', ')

    const paidHint = order.payment_timing === 'online'
      ? 'Já pago no site'
      : order.payment_timing === 'on_delivery'
        ? 'Pagar na entrega'
        : 'Pagar na retirada'

    setObservations(
      `Pedido online ${order.order_number} · ${paidHint}${ship ? `\n${ship}` : ''}${order.customer_phone ? `\nTel: ${order.customer_phone}` : ''}`
    )

    return true
  }, [mapOnlineOrderItemsToProducts])

  const handleImportOnlineOrder = useCallback(async (order) => {
    setImportOnlineOrderModalOpen(false)
    setLoading(true)
    try {
      const loaded = await loadOnlineOrderIntoForm(order)
      if (loaded) {
        showMessage(
          'Pedido carregado',
          `Pedido ${order.order_number} carregado.\n\nFinalize a pré-venda para reservar estoque. Use Imprimir para o documento de saída.`,
          'success'
        )
      }
    } catch (error) {
      console.error('Erro ao importar pedido online:', error)
      showMessage(
        'Erro',
        error.response?.data?.message || error.message || 'Erro ao importar pedido',
        'danger'
      )
    } finally {
      setLoading(false)
    }
  }, [loadOnlineOrderIntoForm, showMessage])

  useEffect(() => {
    if (!importOnlineOrderModalOpen) return
    const t = setTimeout(() => {
      loadOnlineOrdersForPresale(onlineOrderSearchTerm)
    }, 250)
    return () => clearTimeout(t)
  }, [importOnlineOrderModalOpen, onlineOrderSearchTerm, loadOnlineOrdersForPresale])

  const handlePrintExitDocument = useCallback(() => {
    if (selectedProducts.length === 0) {
      showMessage('Atenção', 'Adicione itens antes de imprimir', 'warning')
      return
    }
    const customerName = selectedCustomer?.name || 'Cliente'
    const phone = selectedCustomer?.phone || selectedCustomer?.cellphone || ''
    const win = window.open('', '_blank', 'width=800,height=900')
    if (!win) {
      showMessage('Atenção', 'Permita pop-ups para imprimir', 'warning')
      return
    }
    const rows = selectedProducts
      .map(
        (p) =>
          `<tr><td>${p.name}</td><td style="text-align:center">${p.quantity}</td><td style="text-align:right">${formatCurrency(p.unitPrice)}</td><td style="text-align:right">${formatCurrency(p.totalPrice)}</td></tr>`
      )
      .join('')
    win.document.write(`<!DOCTYPE html><html><head><title>Saída pré-venda</title>
      <style>
        body{font-family:system-ui,sans-serif;padding:24px;color:#0f172a}
        h1{font-size:18px;margin:0 0 8px}
        .meta{font-size:13px;color:#475569;margin-bottom:16px;white-space:pre-line}
        table{width:100%;border-collapse:collapse;font-size:13px}
        th,td{border-bottom:1px solid #e2e8f0;padding:8px 4px;text-align:left}
        th{font-size:11px;text-transform:uppercase;color:#64748b}
        .total{margin-top:16px;font-size:16px;font-weight:700;text-align:right}
        .badge{display:inline-block;padding:4px 8px;border-radius:6px;background:#ecfdf5;color:#37971c;font-size:12px;font-weight:700}
        @media print{body{padding:0}}
      </style></head><body>
      <h1>Documento de saída — Pré-venda</h1>
      <div class="meta"><strong>${customerName}</strong>
${phone ? `Tel: ${phone}\n` : ''}${observations || ''}
${importedOnlineOrderNumber ? `\nPedido: ${importedOnlineOrderNumber}` : ''}
${importedOnlineOrderPaid ? '\n<span class="badge">Já pago no site</span>' : '\n<span class="badge" style="background:#fff7ed;color:#9a3412">Pagamento no local</span>'}
      </div>
      <table><thead><tr><th>Item</th><th>Qtd</th><th>Unit.</th><th>Total</th></tr></thead>
      <tbody>${rows}</tbody></table>
      <div class="total">Total: ${formatCurrency(finalTotal)}</div>
      <p style="margin-top:24px;font-size:11px;color:#94a3b8">NFC-e será emitida após o fechamento no caixa.</p>
      <script>window.onload=function(){window.print()}</script>
      </body></html>`)
    win.document.close()
  }, [
    selectedProducts,
    selectedCustomer,
    observations,
    importedOnlineOrderNumber,
    importedOnlineOrderPaid,
    finalTotal,
    showMessage
  ])

  useEffect(() => {
    if (!canImportQuote) return
    const quoteId = searchParams.get('quoteId')
    if (!quoteId || importQuoteHandledRef.current) return

    importQuoteHandledRef.current = true
    handleImportQuoteById(quoteId)
    setSearchParams({}, { replace: true })
  }, [canImportQuote, searchParams, setSearchParams, handleImportQuoteById])

  useEffect(() => {
    if (!importQuoteModalOpen) return
    const timer = setTimeout(() => {
      loadApprovedQuotes(quoteSearchTerm.trim())
    }, 300)
    return () => clearTimeout(timer)
  }, [importQuoteModalOpen, quoteSearchTerm, loadApprovedQuotes])

  // Carregar pré-vendas aguardando finalização
  const loadWaitingPresales = async () => {
    if (!isBrowserOnline()) {
      showMessage(
        'Sem conexão',
        'Pré-vendas reservadas no servidor só podem ser listadas/reabertas com internet.',
        'warning'
      )
      return
    }

    setLoadingWaitingPresales(true)
    try {
      const response = await saleService.getAll({
        type: 'presale',
        status: 'reservation' // Pré-vendas reservadas (finalizadas) aguardando conversão em venda
      })
      
      if (response.success) {
        setWaitingPresales(response.data || [])
        setShowHistoryModal(true)
      }
    } catch (error) {
      console.error('Erro ao carregar pré-vendas aguardando:', error)
      showMessage('Erro', 'Erro ao carregar pré-vendas aguardando finalização', 'danger')
    } finally {
      setLoadingWaitingPresales(false)
    }
  }

  // Selecionar pré-venda e carregar seus dados
  const handleSelectWaitingPresale = async (presale) => {
    if (!isBrowserOnline()) {
      showMessage(
        'Sem conexão',
        'Reabrir pré-venda exige internet (remove reserva de estoque no servidor).',
        'warning'
      )
      return
    }

    // Fechar o modal imediatamente ao clicar na pré-venda
    setWaitingPresalesModalOpen(false)
    
    try {
      setLoading(true)
      
      // Confirmar se o usuário quer reabrir a pré-venda (isso vai remover as reservas)
      showConfirm(
        'Reabrir Pré-venda',
        `Deseja reabrir a pré-venda #${presale.number}?\n\n⚠️ Isso irá remover as reservas de estoque e permitir editar a pré-venda novamente.`,
        'warning',
        async () => {
          try {
            // Reabrir a pré-venda (remove reservas e muda status para 'draft')
            const reopenResponse = await saleService.reopenPresale(presale.id)
            
            if (!reopenResponse.success) {
              throw new Error(reopenResponse.message || 'Erro ao reabrir pré-venda')
            }

            const response = await saleService.getById(presale.id)
            
            if (response.success && response.data) {
              setImportedSaleId(presale.id)
              setImportedQuoteId(null)
              setImportedQuoteNumber(null)

              const loaded = await loadSaleIntoForm(response.data)
              if (!loaded) return

              setWaitingPresales([])
              
              showMessage(
                'Pré-venda carregada',
                `Pré-venda #${presale.number} carregada com sucesso!\n\nAs reservas de estoque foram removidas e você pode editar a pré-venda.`,
                'success'
              )
            } else {
              throw new Error('Erro ao carregar detalhes da pré-venda')
            }
          } catch (error) {
            console.error('Erro ao carregar pré-venda:', error)
            showMessage(
              'Erro',
              error.message || 'Erro ao carregar pré-venda. Tente novamente.',
              'danger'
            )
          } finally {
            setLoading(false)
          }
        }
      )
    } catch (error) {
      console.error('Erro ao selecionar pré-venda:', error)
      setLoading(false)
    }
  }

  // Cadastro rápido de cliente
  const handleQuickCreateCustomer = async () => {
    if (!isBrowserOnline()) {
      showMessage(
        'Sem conexão',
        'Cadastro de cliente exige internet. Selecione um cliente já em cache ou continue sem cliente.',
        'warning'
      )
      return
    }

    if (!quickCreateForm.name.trim()) {
      showMessage('Atenção', 'Nome é obrigatório', 'warning')
      return
    }

    setCreatingCustomer(true)
    try {
      const response = await customerService.createQuick({
        name: quickCreateForm.name.trim(),
        cpfCnpj: quickCreateForm.cpfCnpj.trim() || null,
        email: quickCreateForm.email.trim() || null,
        phone: quickCreateForm.phone.trim() || null
      })

      if (response.success) {
        const filters = { search: customerSearchTerm.trim() }
        const customersResponse = await customerService.getAll(filters)
        if (customersResponse.success) {
          setCustomers(customersResponse.data || [])
          await putCustomersCache(customersResponse.data || [])
        }

        setSelectedCustomer(response.data)
        if (response.data?.id) {
          await putCustomerCache(response.data)
        }
        setQuickCreateModalOpen(false)
        setQuickCreateForm({ name: '', cpfCnpj: '', email: '', phone: '' })
        setCustomerDropdownOpen(false)
        
        const message = response.data.userCreated 
          ? 'Cliente cadastrado com sucesso! Um usuário foi criado para acesso ao ecommerce.'
          : 'Cliente cadastrado com sucesso!'
        showMessage('Sucesso', message, 'success')
      } else {
        throw new Error(response.message || 'Erro ao cadastrar cliente')
      }
    } catch (error) {
      console.error('Erro ao cadastrar cliente:', error)
      showMessage(
        'Erro',
        error.response?.data?.message || error.message || 'Erro ao cadastrar cliente. Tente novamente.',
        'danger'
      )
    } finally {
      setCreatingCustomer(false)
    }
  }

  const handleClearSession = async () => {
    setSelectedProducts([])
    setObservations('')
    setSelectedCustomer(null)
    setSelectedSeller(null)
    setSelectedSellerId(null)
    setSelectedPriceTable(null)
    setSelectedPriceTableName(null)
    setImportedSaleId(null)
    setImportedQuoteId(null)
    setImportedQuoteNumber(null)
    setImportedOnlineOrderId(null)
    setImportedOnlineOrderNumber(null)
    setImportedOnlineOrderPaid(false)
    setFreightOpen(false)
    setFreightQuote(null)
    setDraftRestored(false)
    try {
      if (user?.id) {
        await clearActiveLocalDraft(userIdKey)
      }
    } catch (err) {
      console.warn('[PreVenda] Falha ao limpar rascunho local:', err)
    }
  }

  const handleSyncPending = useCallback(async ({ silentNetwork = false } = {}) => {
    if (!user?.id || isSyncingPending || !isBrowserOnline()) return null

    setIsSyncingPending(true)
    try {
      const result = await syncPendingPresaleDrafts(userIdKey)
      await refreshPendingCount()

      if (result.networkBlocked > 0) {
        skipAutoSyncUntilReconnectRef.current = true
        if (!silentNetwork && result.synced === 0 && result.failed === 0) {
          // Manual sync com servidor fora: aviso suave, não “conflito”
          showMessage(
            'Servidor indisponível',
            'Não foi possível conectar ao servidor. As pré-vendas continuam na fila local e serão sincronizadas quando a API voltar.',
            'warning'
          )
        }
        return result
      }

      if (result.synced > 0 && result.failed === 0) {
        skipAutoSyncUntilReconnectRef.current = false
        showMessage(
          'Pré-vendas sincronizadas',
          `${result.synced} pré-venda(s) enviada(s) e estoque reservado no servidor.`,
          'success'
        )
      } else if (result.failed > 0) {
        const businessErrors = result.errors.filter((e) => e.kind === 'business')
        const detail = businessErrors[0]?.message || result.errors[0]?.message || 'Verifique estoque e tente novamente.'
        showMessage(
          'Conflito ao sincronizar',
          `${result.synced} ok, ${result.failed} com erro.\n\n${detail}\n\nO rascunho permanece editável na fila local.`,
          'danger'
        )
      }

      return result
    } catch (error) {
      console.error('Erro ao sincronizar pré-vendas:', error)
      if (isNetworkFailure(error)) {
        skipAutoSyncUntilReconnectRef.current = true
        if (!silentNetwork) {
          showMessage(
            'Servidor indisponível',
            'Não foi possível conectar ao servidor. As pré-vendas continuam na fila local.',
            'warning'
          )
        }
        return null
      }
      showMessage(
        'Erro',
        error.message || 'Não foi possível sincronizar as pré-vendas pendentes.',
        'danger'
      )
      return null
    } finally {
      setIsSyncingPending(false)
    }
  }, [user?.id, userIdKey, isSyncingPending, refreshPendingCount, showMessage])

  // Sync automático: só na abertura (1x) ou ao reconectar — nunca em loop se a API estiver fora
  useEffect(() => {
    const cameOnline = prevOfflineRef.current && !isOffline
    prevOfflineRef.current = isOffline

    if (cameOnline) {
      skipAutoSyncUntilReconnectRef.current = false
    }

    if (isOffline || !user?.id || pendingSyncCount === 0 || isSyncingPending) return
    if (skipAutoSyncUntilReconnectRef.current && !cameOnline) return

    const shouldAutoSync = cameOnline || !initialPendingSyncRef.current
    if (!shouldAutoSync) return

    initialPendingSyncRef.current = true
    handleSyncPending({ silentNetwork: true })
  }, [isOffline, user?.id, pendingSyncCount, isSyncingPending, handleSyncPending])

  // Finalizar pré-venda
  const handleFinalizePresale = async () => {
    if (selectedProducts.length === 0) {
      showMessage('Atenção', 'Adicione pelo menos um produto ao pedido', 'warning')
      return
    }

    if (!selectedSellerId) {
      showMessage('Atenção', 'Selecione um vendedor antes de finalizar a pré-venda', 'warning')
      return
    }

    const offlineHint = !isBrowserOnline()
      ? '\n\n📡 Sem conexão: a pré-venda será salva localmente. A reserva de estoque só ocorrerá ao reconectar.'
      : '\n\n⚠️ ATENÇÃO: Pré-vendas NÃO alteram o estoque, apenas reservam.'

    showConfirm(
      'Finalizar Pré-venda',
      `Deseja finalizar esta pré-venda?${offlineHint}`,
      'warning',
      async () => {
        await executeFinalizePresale()
      }
    )
  }

  const syncSaleItems = async (saleId) => {
    const saleResponse = await saleService.getById(saleId)
    const existingItems = saleResponse.data?.items || []

    for (const item of existingItems) {
      await saleService.removeItem(saleId, item.id)
    }

    for (const product of selectedProducts) {
      const calc = calculateProductTotal(product)
      const discountPercent = product.discountType === 'percent' ? product.discount : 0

      await saleService.addItem(saleId, {
        productId: product.id,
        quantity: product.quantity,
        unitPrice: product.unitPrice,
        discountPercent: discountPercent || 0,
        discountAmount: calc.discountAmount || 0,
        totalPrice: calc.finalTotal
      })
    }
  }

  const executeFinalizePresale = async () => {
    setIsSaving(true)
    setLoading(true)
    try {
      const draftPayload = {
        items: selectedProducts,
        sellerId: selectedSellerId,
        sellerName: selectedSeller,
        customerId: selectedCustomer?.id || null,
        customerSnapshot: selectedCustomer || null,
        priceTableId: selectedPriceTable,
        priceTableName: selectedPriceTableName,
        observations,
        importedSaleId,
        importedQuoteId,
        importedQuoteNumber,
        importedOnlineOrderId,
        importedOnlineOrderNumber,
        importedOnlineOrderPaid,
        totals: {
          subtotal,
          discount: totalDiscountValue,
          surcharge: totalSurchargeValue,
          final: finalTotal,
        },
      }

      // Offline: enfileira rascunho — NÃO reserva estoque
      if (!isBrowserOnline()) {
        skipAutoSyncUntilReconnectRef.current = true
        await enqueuePendingDraft(userIdKey, draftPayload)
        await refreshPendingCount()
        await handleClearSession()
        showMessage(
          'Rascunho salvo localmente',
          'Sem conexão. A pré-venda foi salva neste dispositivo.\n\nA reserva de estoque será feita automaticamente ao reconectar.',
          'warning'
        )
        return
      }

      let saleId = importedSaleId
      let resolvedCustomerId = selectedCustomer?.id || null

      if (importedQuoteId && !saleId) {
        const fromQuoteResponse = await saleService.createFromQuote(importedQuoteId, { type: 'presale' })
        if (!fromQuoteResponse.success || !fromQuoteResponse.data?.sale?.id) {
          throw new Error(fromQuoteResponse.message || 'Erro ao converter orçamento em pré-venda')
        }
        saleId = fromQuoteResponse.data.sale.id
        if (fromQuoteResponse.data.sale.customer_id) {
          resolvedCustomerId = fromQuoteResponse.data.sale.customer_id
        }
      }

      if (importedOnlineOrderId && !saleId) {
        const fromOrderResponse = await saleService.createFromOnlineOrder(importedOnlineOrderId, { type: 'presale' })
        if (!fromOrderResponse.success || !fromOrderResponse.data?.sale?.id) {
          throw new Error(fromOrderResponse.message || 'Erro ao converter pedido online em pré-venda')
        }
        saleId = fromOrderResponse.data.sale.id
        if (fromOrderResponse.data.sale.customer_id) {
          resolvedCustomerId = fromOrderResponse.data.sale.customer_id
        }
      }

      if (saleId) {
        const updateResponse = await saleService.update(saleId, {
          customerId: resolvedCustomerId,
          saleDate: new Date().toISOString(),
          totalAmount: subtotal,
          discountAmount: totalDiscountValue,
          taxAmount: totalSurchargeValue,
          finalAmount: finalTotal,
          observation: observations,
          sellerId: selectedSellerId || null
        })

        if (!updateResponse.success) {
          throw new Error(updateResponse.message || 'Erro ao atualizar pré-venda')
        }

        await syncSaleItems(saleId)
      } else {
        const saleData = {
          type: 'presale',
          customerId: selectedCustomer?.id || null,
          saleDate: new Date().toISOString(),
          totalAmount: subtotal,
          discountAmount: totalDiscountValue,
          taxAmount: totalSurchargeValue,
          finalAmount: finalTotal,
          status: 'draft',
          observation: observations,
          sellerId: selectedSellerId || null
        }

        const createResponse = await saleService.create(saleData)
        
        if (!createResponse.success) {
          throw new Error(createResponse.message || 'Erro ao criar pré-venda')
        }

        saleId = createResponse.data.id
        await syncSaleItems(saleId)
      }

      const finalizeResponse = await saleService.finalizePresale(saleId)

      if (!finalizeResponse.success) {
        throw new Error(finalizeResponse.message || 'Erro ao finalizar pré-venda')
      }

      try {
        await clearActiveLocalDraft(userIdKey)
      } catch {
        /* ignore */
      }

      showMessage(
        'Pré-venda finalizada',
        importedOnlineOrderId
          ? 'Pré-venda emitida!\n\nO pedido online passou para "Pré-venda emitida". Você pode baixar o documento na Pré-venda (Imprimir) ou em Vendas Online.\n\n⚠️ Estoque RESERVADO — NFC-e só no caixa.'
          : 'Pré-venda finalizada com sucesso!\n\n⚠️ Lembre-se: O estoque foi RESERVADO, mas não foi baixado.\n\nA pré-venda está aguardando finalização na página de Vendas.',
        'success',
        () => {
          handleClearSession()
        }
      )
    } catch (error) {
      console.error('Erro ao finalizar pré-venda:', error)

      // Falha de rede no meio do fluxo → enfileira para sync
      if (isNetworkFailure(error)) {
        try {
          skipAutoSyncUntilReconnectRef.current = true
          await enqueuePendingDraft(userIdKey, {
            items: selectedProducts,
            sellerId: selectedSellerId,
            sellerName: selectedSeller,
            customerId: selectedCustomer?.id || null,
            customerSnapshot: selectedCustomer || null,
            priceTableId: selectedPriceTable,
            priceTableName: selectedPriceTableName,
            observations,
            importedSaleId,
            importedQuoteId,
            importedQuoteNumber,
            totals: {
              subtotal,
              discount: totalDiscountValue,
              surcharge: totalSurchargeValue,
              final: finalTotal,
            },
          })
          await refreshPendingCount()
          await handleClearSession()
          showMessage(
            'Rascunho salvo localmente',
            'Servidor indisponível. A pré-venda foi salva neste dispositivo.\n\nA reserva de estoque será feita automaticamente quando a API voltar (ou ao clicar em Sincronizar).',
            'warning'
          )
          return
        } catch (enqueueErr) {
          console.error('Erro ao enfileirar rascunho:', enqueueErr)
        }
      }

      const errorMessage = error.response?.data?.message || error.message || 'Erro ao finalizar pré-venda. Tente novamente.'
      
      // Melhorar formatação da mensagem de estoque insuficiente
      let formattedMessage = errorMessage
      if (errorMessage.includes('Estoque insuficiente')) {
        // Extrair informações da mensagem
        const productMatch = errorMessage.match(/produto "([^"]+)"/)
        const availableMatch = errorMessage.match(/Disponível: (\d+)/)
        const requiredMatch = errorMessage.match(/Necessário: (\d+)/)
        
        if (productMatch && availableMatch && requiredMatch) {
          const productName = productMatch[1]
          const available = availableMatch[1]
          const required = requiredMatch[1]
          const missing = parseInt(required) - parseInt(available)
          
          formattedMessage = (
            <div style={{ textAlign: 'left' }}>
              <p style={{ marginBottom: '1rem', fontWeight: 500 }}>
                ⚠️ <strong>Estoque insuficiente</strong>
              </p>
              <div style={{ 
                background: '#fef2f2', 
                border: '1px solid #fecaca', 
                borderRadius: '8px', 
                padding: '1rem',
                marginBottom: '1rem'
              }}>
                <p style={{ margin: '0 0 0.5rem 0', fontWeight: 600, color: '#991b1b' }}>
                  Produto: <span style={{ color: '#111827' }}>{productName}</span>
                </p>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '0.25rem', fontSize: '0.9375rem' }}>
                  <span>📦 Disponível: <strong style={{ color: '#059669' }}>{available}</strong></span>
                  <span>🛒 Necessário: <strong style={{ color: '#dc2626' }}>{required}</strong></span>
                  <span>❌ Faltam: <strong style={{ color: '#dc2626' }}>{missing}</strong> unidade(s)</span>
                </div>
              </div>
              <p style={{ margin: 0, fontSize: '0.875rem', color: '#6b7280' }}>
                Por favor, ajuste a quantidade ou verifique o estoque disponível.
              </p>
            </div>
          )
        }
      }
      
      showMessage('Erro de Estoque', formattedMessage, 'danger')
    } finally {
      setLoading(false)
      setIsSaving(false)
    }
  }


  return (
    <div className="pre-venda-page-new">
      {(isOffline || pendingSyncCount > 0 || draftRestored) && (
        <div
          className={`pre-venda-offline-banner${isOffline ? ' is-offline' : ''}${pendingSyncCount > 0 ? ' has-pending' : ''}`}
          role="status"
        >
          <div className="pre-venda-offline-banner-left">
            {isOffline ? <WifiOff size={16} /> : <Info size={16} />}
            <div className="pre-venda-offline-banner-text">
              {isOffline ? (
                <span>
                  Offline — catálogo e rascunho locais. Estoque exibido é o último conhecido (sujeito a confirmação).
                  Reserva no servidor só ocorre ao reconectar.
                </span>
              ) : pendingSyncCount > 0 ? (
                <span>
                  {pendingSyncCount} pré-venda(s) pendente(s) de sincronização (rascunho local, estoque ainda não reservado).
                </span>
              ) : (
                <span>
                  Rascunho local restaurado — ainda não é pré-venda reservada no servidor.
                </span>
              )}
            </div>
          </div>
          {pendingSyncCount > 0 && !isOffline && (
            <button
              type="button"
              className="pre-venda-offline-banner-sync"
              onClick={() => handleSyncPending()}
              disabled={isSyncingPending}
            >
              {isSyncingPending ? <Loader2 size={14} className="spinning" /> : <RefreshCcw size={14} />}
              Sincronizar
            </button>
          )}
        </div>
      )}

      {/* Top Bar - Pró Médico */}
      <div className="pre-venda-top-bar">
        <div className="pre-venda-top-bar-left">
          <div className="pre-venda-logo">
            <img
              src={`${process.env.PUBLIC_URL}/favicon.svg`}
              alt="Pró Médico"
              className="pre-venda-logo-icon pre-venda-logo-icon--app"
            />
            <div>
              <h1 className="pre-venda-logo-title">Pró Médico <span className="pre-venda-logo-pos">POS</span></h1>
              <p className="pre-venda-logo-subtitle">Terminal de Pré-Vendas</p>
            </div>
          </div>
        </div>

        {/* Central Info Badges */}
        <div className="pre-venda-top-bar-center">
          {selectedSeller && (
            <div className="pre-venda-info-badge">
              <UserRoundPlus size={12} className="pre-venda-info-badge-icon" />
              <span className="pre-venda-info-badge-text">Vendedor: {selectedSeller}</span>
            </div>
          )}
          {selectedCustomer && (
            <div className="pre-venda-info-badge">
              <UserRound size={12} className="pre-venda-info-badge-icon" />
              <span className="pre-venda-info-badge-text">{selectedCustomer.name}</span>
            </div>
          )}
          {selectedPriceTableName && (
            <div className="pre-venda-info-badge">
              <Table2 size={12} className="pre-venda-info-badge-icon amber" />
              <span className="pre-venda-info-badge-text">{selectedPriceTableName}</span>
            </div>
          )}
          {importedQuoteNumber && (
            <div className="pre-venda-info-badge">
              <FileHeart size={12} className="pre-venda-info-badge-icon" />
              <span className="pre-venda-info-badge-text">Orçamento #{importedQuoteNumber}</span>
            </div>
          )}
          {importedOnlineOrderNumber && (
            <div className="pre-venda-info-badge">
              <Package size={12} className="pre-venda-info-badge-icon" />
              <span className="pre-venda-info-badge-text">
                Pedido {importedOnlineOrderNumber}
                {importedOnlineOrderPaid ? ' · pago' : ' · local'}
              </span>
            </div>
          )}
        </div>

        <div className="pre-venda-top-bar-right">
          {canAccessSellerAssistant && (
            <button
              type="button"
              onClick={() => setSellerAssistantOpen((open) => !open)}
              className={`pre-venda-top-bar-icon-btn pre-venda-top-bar-icon-btn--assistant${sellerAssistantOpen ? ' pre-venda-top-bar-icon-btn--active' : ''}`}
              title="Assistente de vendas"
              aria-label="Assistente de vendas"
            >
              <BrainCircuit size={18} />
            </button>
          )}
          {canImportQuote && (
            <button
              type="button"
              onClick={() => {
                setImportQuoteModalOpen(true)
                loadApprovedQuotes('')
              }}
              className="pre-venda-top-bar-icon-btn"
              title="Importar orçamento aprovado"
            >
              <FileHeart size={18} />
            </button>
          )}
          {canImportOnlineOrder && (
            <button
              type="button"
              onClick={() => {
                setImportOnlineOrderModalOpen(true)
                setOnlineOrderSearchTerm('')
                loadOnlineOrdersForPresale('')
              }}
              className="pre-venda-top-bar-icon-btn"
              title="Importar pedido online aguardando pré-venda"
            >
              <Package size={18} />
              {onlineOrdersForPresale.length > 0 && (
                <span className="pre-venda-top-bar-icon-badge pre-venda-top-bar-icon-badge--alert" />
              )}
            </button>
          )}
          <button
            type="button"
            onClick={handlePrintExitDocument}
            className="pre-venda-top-bar-icon-btn"
            title="Imprimir documento de saída"
            disabled={selectedProducts.length === 0}
          >
            <Printer size={18} />
          </button>
          <button
            type="button"
            onClick={loadWaitingPresales}
            className="pre-venda-top-bar-icon-btn"
            title="Pré-vendas aguardando"
          >
            <Clock size={18} />
            {waitingPresales.length > 0 && <span className="pre-venda-top-bar-icon-badge" />}
          </button>

          <div className="pre-venda-top-bar-divider" />

          <button
            type="button"
            onClick={() => {
              setSellerModalOpen(true)
              setSellerSearchTerm('')
            }}
            className={`pre-venda-top-bar-icon-btn${selectedSellerId ? ' pre-venda-top-bar-icon-btn--active' : ''}`}
            title={selectedSeller ? `Vendedor: ${selectedSeller}` : 'Selecionar vendedor'}
          >
            <UserRoundPlus size={18} />
          </button>
          <button
            type="button"
            onClick={() => setCustomerDropdownOpen(true)}
            className={`pre-venda-top-bar-icon-btn${selectedCustomer ? ' pre-venda-top-bar-icon-btn--active' : ''}`}
            title={selectedCustomer ? `Cliente: ${selectedCustomer.name}` : 'Selecionar cliente'}
          >
            <UserRound size={18} />
          </button>
          <button
            type="button"
            ref={freightBtnRef}
            onClick={() => setFreightOpen((open) => !open)}
            className={`pre-venda-top-bar-icon-btn${freightQuote ? ' pre-venda-top-bar-icon-btn--active' : ''}${freightOpen ? ' pre-venda-top-bar-icon-btn--active' : ''}`}
            title={
              freightQuote
                ? `Frete: ${formatCurrency(freightQuote.price)}`
                : 'Calcular frete por CEP'
            }
            aria-label="Calcular frete por CEP"
            aria-expanded={freightOpen}
          >
            <MapPin size={18} />
            {freightQuote && <span className="pre-venda-top-bar-icon-badge" />}
          </button>
          <button
            type="button"
            onClick={openPriceTableModal}
            className={`pre-venda-top-bar-icon-btn${selectedPriceTable ? ' pre-venda-top-bar-icon-btn--active' : ''}`}
            title={selectedPriceTableName ? `Tabela: ${selectedPriceTableName}` : 'Selecionar tabela de preços'}
          >
            <Table2 size={18} />
          </button>

          <div className="pre-venda-top-bar-divider" />

          <button 
            onClick={() => setShowUserModal(true)} 
            className="pre-venda-top-bar-user-btn"
            ref={userMenuRef}
            title={user?.name || 'Usuário'}
            aria-label={user?.name ? `Menu do usuário: ${user.name}` : 'Menu do usuário'}
          >
            <div className="pre-venda-top-bar-user-avatar">
              <img src={user?.avatar_url || `https://ui-avatars.com/api/?name=${encodeURIComponent(user?.name || user?.email || 'U')}&background=1e3a8a&color=fff`} alt={user?.name || 'Usuário'} />
            </div>
            <ChevronDown size={14} />
          </button>
        </div>
      </div>

      <PreVendaFreightDropdown
        open={freightOpen}
        onClose={() => setFreightOpen(false)}
        buttonRef={freightBtnRef}
        selectedCustomer={selectedCustomer}
        onQuoteChange={setFreightQuote}
      />

      {/* Layout Principal */}
      <div className="pre-venda-main-layout">
        <div className="pre-venda-main-content">
          <div className="pre-venda-search-section">
            <ERPProductSearch 
              onSelect={handleAddProduct} 
              placeholder="Localizar produto por nome, SKU ou Código (F2)..."
              showCamera
            />
          </div>

          <div className="pre-venda-cart-container">
            <div className="pre-venda-cart-header">
              <div className="pre-venda-cart-header-left">
                <div className="pre-venda-cart-header-icon">
                  <ShoppingCart size={16} />
                </div>
                <span className="pre-venda-cart-header-title">
                  Itens na Sessão ({selectedProducts.length})
                </span>
              </div>
            </div>

            <div className="pre-venda-cart-body">
              {selectedProducts.length === 0 ? (
                <div className="pre-venda-cart-empty">
                  <div className="pre-venda-cart-empty-icon">
                    <ShoppingCart size={48} />
                  </div>
                  <p className="pre-venda-cart-empty-title">Aguardando Insumos</p>
                  <p className="pre-venda-cart-empty-subtitle">Bipe ou digite para começar</p>
                </div>
              ) : (
                selectedProducts.map((item, index) => {
                  const calc = calculateProductTotal(item)
                  return (
                    <div key={`${item.id}-${index}`} className="pre-venda-cart-item">
                      <div className="pre-venda-cart-item-left">
                        <div className="pre-venda-cart-item-image">
                          {item.image_url ? (
                            <img src={item.image_url} alt={item.name} />
                          ) : (
                            <ShoppingCart size={20} />
                          )}
                        </div>
                        <div className="pre-venda-cart-item-info">
                          <div className="pre-venda-cart-item-meta">
                            {item.sku && (
                              <>
                                <span className="pre-venda-cart-item-sku">{item.sku}</span>
                                <div className="pre-venda-cart-item-dot" />
                              </>
                            )}
                            {item.brand && (
                              <span className="pre-venda-cart-item-brand">{item.brand}</span>
                            )}
                          </div>
                          <h4 className="pre-venda-cart-item-name">{item.name}</h4>
                          <p className="pre-venda-cart-item-unit">Unitário: {formatCurrency(item.unitPrice)}</p>
                        </div>
                      </div>

                      <div className="pre-venda-cart-item-right">
                        <div className="pre-venda-cart-item-control">
                          <span className="pre-venda-cart-item-label">Qtd</span>
                          <div className="pre-venda-cart-item-quantity">
                            <button 
                              type="button"
                              onClick={() => handleUpdateItem(index, 'quantity', Math.max(1, item.quantity - 1))}
                              className="pre-venda-cart-item-qty-btn"
                              disabled={item.fromQuote}
                              title={item.fromQuote ? 'Quantidade fixa para itens importados de orçamento' : ''}
                            >
                              <Minus size={14} />
                            </button>
                            <input 
                              type="number" 
                              className="pre-venda-cart-item-qty-input"
                              value={item.quantity}
                              disabled={item.fromQuote}
                              title={item.fromQuote ? 'Quantidade fixa para itens importados de orçamento' : ''}
                              onChange={(e) => handleUpdateItem(index, 'quantity', Math.max(1, parseInt(e.target.value) || 1))}
                            />
                            <button 
                              type="button"
                              onClick={() => handleUpdateItem(index, 'quantity', item.quantity + 1)}
                              className="pre-venda-cart-item-qty-btn"
                              disabled={item.fromQuote}
                              title={item.fromQuote ? 'Quantidade fixa para itens importados de orçamento' : ''}
                            >
                              <Plus size={14} />
                            </button>
                          </div>
                        </div>

                        <div className="pre-venda-cart-item-control">
                          <span className="pre-venda-cart-item-label">Desc %</span>
                          <div className="pre-venda-cart-item-discount">
                            <input 
                              type="number"
                              className="pre-venda-cart-item-discount-input"
                              value={item.discountType === 'percent' ? (item.discount || '') : ''}
                              placeholder="0"
                              disabled={selectedPriceTable !== null}
                              title={selectedPriceTable ? 'Desconto desabilitado quando há tabela de preços selecionada' : ''}
                              onChange={(e) => {
                                if (!selectedPriceTable) {
                                  const discount = Math.min(100, Math.max(0, parseFloat(e.target.value) || 0))
                                  handleUpdateProductDiscount(item.id, discount, 'percent')
                                }
                              }}
                            />
                            <Percent size={10} className="pre-venda-cart-item-discount-icon" />
                          </div>
                        </div>

                        <div className="pre-venda-cart-item-total">
                          <span className="pre-venda-cart-item-label">Total</span>
                          <p className="pre-venda-cart-item-total-value">{formatCurrency(calc.finalTotal)}</p>
                        </div>

                        {!item.fromQuote && (
                          <button 
                            onClick={() => handleRemoveProduct(item.id)} 
                            className="pre-venda-cart-item-remove"
                            type="button"
                            title="Remover item"
                          >
                            <Trash2 size={20} />
                          </button>
                        )}
                      </div>
                    </div>
                  )
                })
              )}
            </div>
          </div>
        </div>

        {/* LADO DIREITO: RESUMO CONSOLIDADO */}
        <div className="pre-venda-summary-panel">
          <div className="pre-venda-summary-header">
            <div className="pre-venda-summary-header-left">
              <h3 className="pre-venda-summary-title">
                <Calculator size={18} />
                Resumo
              </h3>
            </div>
            <div className="pre-venda-summary-header-right">
              <div className="pre-venda-summary-badge">
                {totalItemsCount} Unidades
              </div>
            </div>
          </div>

          <div className="pre-venda-summary-content">
            <div className="pre-venda-summary-row">
              <span className="pre-venda-summary-label">Total Bruto</span>
              <div className="pre-venda-summary-divider" />
              <span className="pre-venda-summary-value">{formatCurrency(subtotal)}</span>
            </div>
            
            <div className="pre-venda-summary-row">
              <div className="pre-venda-summary-label-group">
                <span className="pre-venda-summary-label">Descontos</span>
                {totalDiscountValue > 0 && (
                  <div className="pre-venda-summary-sparkle">
                    <Sparkles size={10} />
                  </div>
                )}
              </div>
              <div className="pre-venda-summary-divider" />
              <span className="pre-venda-summary-value discount">- {formatCurrency(totalDiscountValue)}</span>
            </div>

            <div className="pre-venda-summary-row">
              <span className="pre-venda-summary-label">Frete</span>
              <div className="pre-venda-summary-divider" />
              <span
                className={`pre-venda-summary-value${freightQuote ? ' freight' : ' muted'}`}
                title={freightQuote ? 'Valor informativo — não entra no Total Líquido' : 'Informe o CEP no ícone de localização'}
              >
                {freightQuote ? formatCurrency(freightQuote.price) : 'Informe o CEP'}
              </span>
            </div>
          </div>

          <div className="pre-venda-summary-footer">
            <div className="pre-venda-summary-total-card">
              <div className="pre-venda-summary-total-inner">
                <p className="pre-venda-summary-total-label">Total Líquido</p>
                <p className="pre-venda-summary-total-value">{formatCurrency(finalTotal)}</p>
              </div>
            </div>

            <div className="pre-venda-summary-actions">
              <button 
                type="button"
                onClick={handleClearSession}
                disabled={selectedProducts.length === 0 && !selectedCustomer && !importedSaleId && !importedQuoteId && !importedOnlineOrderId}
                className="pre-venda-summary-btn pre-venda-summary-btn-clear"
              >
                <Trash2 size={20} />
                Limpar
              </button>
              <button 
                type="button"
                onClick={handleFinalizePresale}
                disabled={selectedProducts.length === 0 || isSaving || loading}
                className="pre-venda-summary-btn pre-venda-summary-btn-finalize"
              >
                {isSaving
                  ? <Loader2 size={18} className="spinning" />
                  : isOffline
                    ? <WifiOff size={18} />
                    : <CheckCircle2 size={20} />}
                {isOffline ? 'Salvar local' : 'Finalizar'}
              </button>
            </div>
          </div>
        </div>
      </div>


      {canAccessSellerAssistant && (
        <SellerAssistantPanel
          open={sellerAssistantOpen}
          onClose={() => setSellerAssistantOpen(false)}
        />
      )}

      {/* Modal de Alteração de Quantidade */}
      {quantityModalOpen && selectedProductForAction && (
        <ProductQuantityModal
          product={selectedProductForAction}
          onClose={() => {
            setQuantityModalOpen(false)
            setSelectedProductForAction(null)
          }}
          onConfirm={(newQuantity) => {
            handleUpdateProductQuantity(selectedProductForAction.id, newQuantity)
            setQuantityModalOpen(false)
            setSelectedProductForAction(null)
          }}
          calculateProductTotal={calculateProductTotal}
          formatCurrency={formatCurrency}
        />
      )}

      {/* Modal de Desconto */}
      {discountModalOpen && selectedProductForAction && (
        <ProductDiscountModal
          product={selectedProductForAction}
          onClose={() => {
            setDiscountModalOpen(false)
            setSelectedProductForAction(null)
          }}
          onConfirm={(discount, discountType) => {
            handleUpdateProductDiscount(selectedProductForAction.id, discount, discountType)
            setDiscountModalOpen(false)
            setSelectedProductForAction(null)
          }}
          calculateProductTotal={calculateProductTotal}
          formatCurrency={formatCurrency}
        />
      )}

      {/* Modal de Acréscimo */}
      {surchargeModalOpen && selectedProductForAction && (
        <ProductSurchargeModal
          product={selectedProductForAction}
          onClose={() => {
            setSurchargeModalOpen(false)
            setSelectedProductForAction(null)
          }}
          onConfirm={(surcharge, surchargeType) => {
            handleUpdateProductSurcharge(selectedProductForAction.id, surcharge, surchargeType)
            setSurchargeModalOpen(false)
            setSelectedProductForAction(null)
          }}
          calculateProductTotal={calculateProductTotal}
          formatCurrency={formatCurrency}
        />
      )}

      {/* Modal de Usuário */}
      {showUserModal && (
        <div className="pre-venda-modal-overlay" onClick={() => setShowUserModal(false)}>
          <div className="pre-venda-modal-user-content" onClick={(e) => e.stopPropagation()}>
            <div className="pre-venda-modal-user-header">
              <div className="pre-venda-modal-user-avatar-large">
                <img src={user?.avatar_url || `https://ui-avatars.com/api/?name=${encodeURIComponent(user?.name || user?.email || 'U')}&background=1e3a8a&color=fff`} alt="User" />
              </div>
              <div>
                <h3 className="pre-venda-modal-user-name">{user?.name}</h3>
                <p className="pre-venda-modal-user-role">Operador Senior POS</p>
              </div>
            </div>
            <div className="pre-venda-modal-user-body">
              <button 
                onClick={() => {
                  navigate('/erp')
                  setShowUserModal(false)
                }} 
                className="pre-venda-modal-user-btn"
              >
                Retornar ao ERP <ArrowRight size={16} />
              </button>
              <button 
                onClick={() => {
                  logout()
                  setShowUserModal(false)
                }} 
                className="pre-venda-modal-user-btn pre-venda-modal-user-btn-logout"
              >
                <LogOut size={18} /> Encerrar Turno
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal de Seleção de Vendedor */}
      {sellerModalOpen && (
        <div className="pre-venda-modal-overlay" onClick={() => setSellerModalOpen(false)}>
          <div className="pre-venda-modal-content pre-venda-modal-select" onClick={(e) => e.stopPropagation()}>
            <div className="pre-venda-modal-header">
              <div className="pre-venda-modal-header-left">
                <div className="pre-venda-modal-icon">
                  <UserRoundPlus size={24} />
                </div>
                <div>
                  <h3 className="pre-venda-modal-title">Selecionar Vendedor</h3>
                  <p className="pre-venda-modal-subtitle">Obrigatório para finalizar a pré-venda</p>
                </div>
              </div>
              <button onClick={() => setSellerModalOpen(false)} className="pre-venda-modal-close">
                <X size={24} />
              </button>
            </div>

            <div className="pre-venda-modal-body">
              <div className="pre-venda-search-box">
                <Search size={18} className="pre-venda-search-icon" />
                <input
                  type="text"
                  className="pre-venda-search-input"
                  placeholder="Buscar por nome ou e-mail..."
                  value={sellerSearchTerm}
                  onChange={(e) => setSellerSearchTerm(e.target.value)}
                  autoFocus
                />
              </div>

              <div className="pre-venda-select-list">
                {filteredSellers.length > 0 ? (
                  filteredSellers.map(seller => (
                    <div
                      key={seller.id}
                      className={`pre-venda-select-item ${selectedSellerId === seller.id ? 'selected' : ''}`}
                      onClick={() => {
                        setSelectedSellerId(seller.id)
                        setSelectedSeller(seller.name)
                        setSellerModalOpen(false)
                        setSellerSearchTerm('')
                      }}
                    >
                      <div className="pre-venda-select-item-avatar">
                        <UserRoundPlus size={20} />
                      </div>
                      <div className="pre-venda-select-item-info">
                        <div className="pre-venda-select-item-name">{seller.name}</div>
                        {seller.email && (
                          <div className="pre-venda-select-item-detail">{seller.email}</div>
                        )}
                      </div>
                      {selectedSellerId === seller.id && (
                        <CheckCircle size={20} className="pre-venda-select-item-check" />
                      )}
                    </div>
                  ))
                ) : (
                  <div className="pre-venda-select-empty">
                    <UserRoundPlus size={48} />
                    <p>{sellerSearchTerm.trim() ? 'Nenhum vendedor encontrado' : 'Nenhum vendedor disponível'}</p>
                  </div>
                )}
              </div>
            </div>

            {selectedSellerId && (
              <div className="pre-venda-modal-footer">
                <button
                  className="pre-venda-modal-btn-secondary"
                  onClick={() => {
                    setSelectedSellerId(null)
                    setSelectedSeller(null)
                    setSellerModalOpen(false)
                  }}
                >
                  <X size={18} />
                  Remover Vendedor
                </button>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Modal de Confirmação */}
      <ConfirmModal
        isOpen={confirmModal.isOpen}
        onClose={() => setConfirmModal(prev => ({ ...prev, isOpen: false }))}
        onConfirm={confirmModal.onConfirm}
        title={confirmModal.title}
        message={confirmModal.message}
        type={confirmModal.type}
        confirmText={confirmModal.confirmText}
        cancelText={confirmModal.cancelText}
        showCancel={confirmModal.showCancel}
      />

      {/* Modal de Seleção de Cliente */}
      {customerDropdownOpen && (
        <div className="pre-venda-modal-overlay" onClick={() => setCustomerDropdownOpen(false)}>
          <div className="pre-venda-modal-content pre-venda-modal-select" onClick={(e) => e.stopPropagation()}>
            <div className="pre-venda-modal-header">
              <div className="pre-venda-modal-header-left">
                <div className="pre-venda-modal-icon">
                  <UserRound size={24} />
                </div>
                <div>
                  <h3 className="pre-venda-modal-title">Selecionar Cliente</h3>
                  <p className="pre-venda-modal-subtitle">Escolha o cliente para esta pré-venda</p>
                </div>
              </div>
              <button onClick={() => setCustomerDropdownOpen(false)} className="pre-venda-modal-close">
                <X size={24} />
              </button>
            </div>

            <div className="pre-venda-modal-body">
              <div className="pre-venda-search-box">
                <Search size={18} className="pre-venda-search-icon" />
                <input
                  type="text"
                  className="pre-venda-search-input"
                  placeholder="Buscar por nome ou documento..."
                  value={customerSearchTerm}
                  onChange={(e) => setCustomerSearchTerm(e.target.value)}
                  autoFocus
                />
                {loadingCustomers && (
                  <div className="pre-venda-search-loading">
                    <Loader2 size={16} className="spinning" />
                  </div>
                )}
              </div>

              <div className="pre-venda-select-list">
                {loadingCustomers ? (
                  <div className="pre-venda-select-empty">
                    <Loader2 size={32} className="spinning" />
                    <p>Buscando clientes...</p>
                  </div>
                ) : customers.length > 0 ? (
                  customers.map(customer => (
                    <div
                      key={customer.id}
                      className={`pre-venda-select-item ${selectedCustomer?.id === customer.id ? 'selected' : ''}`}
                      onClick={() => {
                        setSelectedCustomer(customer)
                        putCustomerCache(customer).catch(() => {})
                        setCustomerDropdownOpen(false)
                        setCustomerSearchTerm('')
                      }}
                    >
                      <div className="pre-venda-select-item-avatar">
                        <UserRound size={20} />
                      </div>
                      <div className="pre-venda-select-item-info">
                        <div className="pre-venda-select-item-name">{customer.name}</div>
                        {(customer.document || customer.cpf_cnpj || customer.cpfCnpj) && (
                          <div className="pre-venda-select-item-detail">
                            {customer.document || customer.cpf_cnpj || customer.cpfCnpj}
                          </div>
                        )}
                      </div>
                      {selectedCustomer?.id === customer.id && (
                        <CheckCircle size={20} className="pre-venda-select-item-check" />
                      )}
                    </div>
                  ))
                ) : (
                  <div className="pre-venda-select-empty">
                    <UserRound size={48} />
                    <p>{customerSearchTerm.trim() ? 'Nenhum cliente encontrado' : 'Digite para buscar clientes'}</p>
                  </div>
                )}
              </div>
            </div>

            <div className="pre-venda-modal-footer" style={{ display: 'flex', gap: '0.75rem', flexWrap: 'wrap', alignItems: 'center' }}>
              {!isOffline && (
                <button
                  className="pre-venda-modal-btn-secondary"
                  type="button"
                  onClick={() => setQuickCreateModalOpen(true)}
                >
                  <UserRoundPlus size={18} />
                  Cadastro rápido
                </button>
              )}
              {isOffline && (
                <p className="pre-venda-offline-customer-hint">
                  Offline: apenas clientes já em cache. Cadastro rápido indisponível.
                </p>
              )}
              {selectedCustomer && (
                <button
                  className="pre-venda-modal-btn-secondary"
                  type="button"
                  onClick={() => {
                    setSelectedCustomer(null)
                    setCustomerDropdownOpen(false)
                  }}
                >
                  <X size={18} />
                  Remover Cliente
                </button>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Modal de Importar Orçamento */}
      {importQuoteModalOpen && (
        <div className="pre-venda-modal-overlay" onClick={() => setImportQuoteModalOpen(false)}>
          <div className="pre-venda-modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="pre-venda-modal-header">
              <div className="pre-venda-modal-header-left">
                <div className="pre-venda-modal-icon">
                  <FileHeart size={24} />
                </div>
                <div>
                  <h3 className="pre-venda-modal-title">Importar Orçamento</h3>
                  <p className="pre-venda-modal-subtitle">Orçamentos aprovados disponíveis para pré-venda</p>
                </div>
              </div>
              <button onClick={() => setImportQuoteModalOpen(false)} className="pre-venda-modal-close">
                <X size={24} />
              </button>
            </div>

            <div className="pre-venda-modal-body">
              <div className="pre-venda-search-box">
                <Search size={18} className="pre-venda-search-icon" />
                <input
                  type="text"
                  className="pre-venda-search-input"
                  placeholder="Buscar por número, cliente..."
                  value={quoteSearchTerm}
                  onChange={(e) => setQuoteSearchTerm(e.target.value)}
                  autoFocus
                />
                {loadingApprovedQuotes && (
                  <div className="pre-venda-search-loading">
                    <Loader2 size={16} className="spinning" />
                  </div>
                )}
              </div>

              <div className="pre-venda-select-list">
                {loadingApprovedQuotes ? (
                  <div className="pre-venda-select-empty">
                    <Loader2 size={32} className="spinning" />
                    <p>Buscando orçamentos...</p>
                  </div>
                ) : approvedQuotes.length > 0 ? (
                  approvedQuotes.map(quote => (
                    <div
                      key={quote.id}
                      className="pre-venda-select-item"
                      onClick={() => handleImportQuote(quote)}
                    >
                      <div className="pre-venda-select-item-avatar">
                        <FileHeart size={20} />
                      </div>
                      <div className="pre-venda-select-item-info">
                        <div className="pre-venda-select-item-name">
                          Orçamento #{quote.number || quote.id}
                        </div>
                        <div className="pre-venda-select-item-detail">
                          {quote.customer_name || 'Cliente não informado'}
                          {' • '}
                          {formatCurrency(quote.final_amount || quote.total_amount || 0)}
                        </div>
                        {quote.validity_date && (
                          <div className="pre-venda-select-item-detail">
                            Validade: {new Date(quote.validity_date).toLocaleDateString('pt-BR')}
                          </div>
                        )}
                      </div>
                      <ArrowRight size={18} />
                    </div>
                  ))
                ) : (
                  <div className="pre-venda-select-empty">
                    <FileHeart size={48} />
                    <p>{quoteSearchTerm.trim() ? 'Nenhum orçamento encontrado' : 'Nenhum orçamento aprovado disponível'}</p>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Modal de Importar Pedido Online */}
      {importOnlineOrderModalOpen && (
        <div className="pre-venda-modal-overlay" onClick={() => setImportOnlineOrderModalOpen(false)}>
          <div className="pre-venda-modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="pre-venda-modal-header">
              <div className="pre-venda-modal-header-left">
                <div className="pre-venda-modal-icon">
                  <Package size={24} />
                </div>
                <div>
                  <h3 className="pre-venda-modal-title">Pedidos aguardando pré-venda</h3>
                  <p className="pre-venda-modal-subtitle">Pedidos online prontos para emitir documento de saída</p>
                </div>
              </div>
              <button onClick={() => setImportOnlineOrderModalOpen(false)} className="pre-venda-modal-close">
                <X size={24} />
              </button>
            </div>

            <div className="pre-venda-modal-body">
              <div className="pre-venda-search-box">
                <Search size={18} className="pre-venda-search-icon" />
                <input
                  type="text"
                  className="pre-venda-search-input"
                  placeholder="Buscar por número, cliente, CPF..."
                  value={onlineOrderSearchTerm}
                  onChange={(e) => setOnlineOrderSearchTerm(e.target.value)}
                  autoFocus
                />
                {loadingOnlineOrders && (
                  <div className="pre-venda-search-loading">
                    <Loader2 size={16} className="spinning" />
                  </div>
                )}
              </div>

              <div className="pre-venda-select-list">
                {loadingOnlineOrders ? (
                  <div className="pre-venda-select-empty">
                    <Loader2 size={32} className="spinning" />
                    <p>Buscando pedidos...</p>
                  </div>
                ) : onlineOrdersForPresale.length > 0 ? (
                  onlineOrdersForPresale.map((order) => (
                    <div
                      key={order.id}
                      className="pre-venda-select-item"
                      onClick={() => handleImportOnlineOrder(order)}
                    >
                      <div className="pre-venda-select-item-avatar">
                        <Package size={20} />
                      </div>
                      <div className="pre-venda-select-item-info">
                        <div className="pre-venda-select-item-name">
                          {order.order_number}
                          {order.paid_online ? ' · Já pago' : ' · Pagar no local'}
                        </div>
                        <div className="pre-venda-select-item-detail">
                          {order.customer_name || 'Cliente não informado'}
                          {' • '}
                          {formatCurrency(order.total_amount || 0)}
                        </div>
                        <div className="pre-venda-select-item-detail">
                          {order.payment_timing === 'on_delivery'
                            ? 'Pagar na entrega'
                            : order.payment_timing === 'on_pickup'
                              ? 'Pagar na retirada'
                              : order.payment_method || 'Online'}
                          {order.shipping_city ? ` • ${order.shipping_city}` : ''}
                        </div>
                      </div>
                      <ArrowRight size={18} />
                    </div>
                  ))
                ) : (
                  <div className="pre-venda-select-empty">
                    <Package size={48} />
                    <p>
                      {onlineOrderSearchTerm.trim()
                        ? 'Nenhum pedido encontrado'
                        : 'Nenhum pedido aguardando na pré-venda'}
                    </p>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Modal de Histórico (Vendas Suspensas) */}
      {showHistoryModal && (
        <div className="pre-venda-modal-overlay" onClick={() => setShowHistoryModal(false)}>
          <div className="pre-venda-modal-content pre-venda-modal-history" onClick={(e) => e.stopPropagation()}>
            <div className="pre-venda-modal-header">
              <div className="pre-venda-modal-header-left">
                <div className="pre-venda-modal-icon">
                  <Clock size={24} />
                </div>
                <div>
                  <h3 className="pre-venda-modal-title">Aguardando Recuperação</h3>
                  <p className="pre-venda-modal-subtitle">Pré-Vendas Suspensas no Terminal</p>
                </div>
              </div>
              <button onClick={() => setShowHistoryModal(false)} className="pre-venda-modal-close">
                <X size={24} />
              </button>
            </div>

            <div className="pre-venda-modal-body">
              {waitingPresales.length > 0 ? (
                <div className="pre-venda-history-list">
                  {waitingPresales.map(presale => (
                    <div key={presale.id} className="pre-venda-history-item">
                      <div className="pre-venda-history-item-left">
                        <div className="pre-venda-history-time">
                          {new Date(presale.created_at).toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })}
                        </div>
                        <div>
                          <p className="pre-venda-history-id">Pré-venda #{presale.number}</p>
                          <h4 className="pre-venda-history-client">{presale.customer_name || 'Cliente não informado'}</h4>
                          <p className="pre-venda-history-details">
                            {presale.items?.length || 0} Itens • Total {formatCurrency(presale.final_amount || presale.total_amount || 0)}
                          </p>
                        </div>
                      </div>
                      <div className="pre-venda-history-item-right">
                        <button 
                          className="pre-venda-history-restore-btn"
                          onClick={() => handleSelectWaitingPresale(presale)}
                        >
                          Restaurar <RefreshCcw size={14} />
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="pre-venda-history-empty">
                  <Clock size={64} />
                  <p>Nenhuma pré-venda em espera</p>
                </div>
              )}
            </div>

            <div className="pre-venda-modal-footer">
              <p className="pre-venda-modal-footer-text">
                <Info size={14} /> Vendas expiram após 24h sem atividade
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Modal de Pré-vendas Aguardando */}
      {waitingPresalesModalOpen && (
        <div className="customer-modal-overlay" onClick={() => setWaitingPresalesModalOpen(false)}>
          <div className="customer-modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="customer-modal-header">
              <div className="customer-modal-title-section">
                <Clock size={24} className="customer-modal-icon" />
                <div>
                  <h2 className="customer-modal-title">Pré-Vendas Aguardando Finalização</h2>
                  <p className="customer-modal-subtitle">
                    Selecione uma pré-venda para reabrir e editar. As reservas de estoque serão removidas.
                  </p>
                </div>
              </div>
              <button
                className="customer-modal-close"
                onClick={() => setWaitingPresalesModalOpen(false)}
                aria-label="Fechar"
              >
                <X size={20} />
              </button>
            </div>

            <div className="customer-modal-body">
              <div className="customer-list-container">
                {loadingWaitingPresales ? (
                  <div className="customer-empty-state">
                    <div className="customer-loading-spinner"></div>
                    <p>Carregando pré-vendas...</p>
                  </div>
                ) : waitingPresales.length > 0 ? (
                  <div className="customer-list">
                    {waitingPresales.map(presale => (
                      <div
                        key={presale.id}
                        className="customer-item"
                        onClick={() => handleSelectWaitingPresale(presale)}
                      >
                        <div className="customer-item-avatar">
                          <Clock size={20} />
                        </div>
                        <div className="customer-item-info">
                          <div className="customer-item-name">Pré-venda #{presale.number}</div>
                          <div className="customer-item-details">
                            {presale.customer_name && (
                              <span className="customer-item-detail">
                                <strong>Cliente:</strong> {presale.customer_name}
                              </span>
                            )}
                            <span className="customer-item-detail">
                              <strong>Total:</strong> {formatCurrency(presale.final_amount || presale.total_amount || 0)}
                            </span>
                            {presale.created_at && (
                              <span className="customer-item-detail">
                                <strong>Data:</strong> {new Date(presale.created_at).toLocaleDateString('pt-BR')}
                              </span>
                            )}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="customer-empty-state">
                    <p>Nenhuma pré-venda aguardando finalização.</p>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Modal de Cadastro Rápido de Cliente */}
      {quickCreateModalOpen && (
        <div className="customer-modal-overlay" onClick={() => {
          if (!creatingCustomer) {
            setQuickCreateModalOpen(false)
            setQuickCreateForm({ name: '', cpfCnpj: '', email: '', phone: '' })
          }
        }}>
          <div className="customer-modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="customer-modal-header">
              <div className="customer-modal-title-section">
                <UserRound size={24} className="customer-modal-icon" />
                <div>
                  <h2 className="customer-modal-title">Cadastro Rápido de Cliente</h2>
                  <p className="customer-modal-subtitle">
                    Preencha os dados básicos. Se informar CPF e E-mail, será criado um usuário para acesso ao ecommerce.
                  </p>
                </div>
              </div>
              <button
                className="customer-modal-close"
                onClick={() => {
                  if (!creatingCustomer) {
                    setQuickCreateModalOpen(false)
                    setQuickCreateForm({ name: '', cpfCnpj: '', email: '', phone: '' })
                  }
                }}
                aria-label="Fechar"
                disabled={creatingCustomer}
              >
                <X size={20} />
              </button>
            </div>

            <div className="customer-modal-body">
              <div className="quick-create-form">
                <div className="quick-create-field">
                  <label className="quick-create-label">
                    Nome <span className="required">*</span>
                  </label>
                  <input
                    type="text"
                    className="quick-create-input"
                    placeholder="Nome completo"
                    value={quickCreateForm.name}
                    onChange={(e) => setQuickCreateForm(prev => ({ ...prev, name: e.target.value }))}
                    disabled={creatingCustomer}
                    autoFocus
                  />
                </div>

                <div className="quick-create-field">
                  <label className="quick-create-label">CPF/CNPJ</label>
                  <input
                    type="text"
                    className="quick-create-input"
                    placeholder="000.000.000-00"
                    value={quickCreateForm.cpfCnpj}
                    onChange={(e) => setQuickCreateForm(prev => ({ ...prev, cpfCnpj: e.target.value }))}
                    disabled={creatingCustomer}
                  />
                </div>

                <div className="quick-create-field">
                  <label className="quick-create-label">E-mail</label>
                  <input
                    type="email"
                    className="quick-create-input"
                    placeholder="email@exemplo.com"
                    value={quickCreateForm.email}
                    onChange={(e) => setQuickCreateForm(prev => ({ ...prev, email: e.target.value }))}
                    disabled={creatingCustomer}
                  />
                </div>

                <div className="quick-create-field">
                  <label className="quick-create-label">Telefone</label>
                  <input
                    type="text"
                    className="quick-create-input"
                    placeholder="(00) 00000-0000"
                    value={quickCreateForm.phone}
                    onChange={(e) => setQuickCreateForm(prev => ({ ...prev, phone: e.target.value }))}
                    disabled={creatingCustomer}
                  />
                </div>

                <div className="quick-create-info">
                  <p>💡 <strong>Dica:</strong> Se você informar CPF e E-mail, um usuário será criado automaticamente para acesso ao ecommerce com uma senha padrão.</p>
                </div>
              </div>
            </div>

            <div className="customer-modal-footer">
              <button
                className="customer-modal-btn customer-modal-btn-remove"
                onClick={() => {
                  if (!creatingCustomer) {
                    setQuickCreateModalOpen(false)
                    setQuickCreateForm({ name: '', cpfCnpj: '', email: '', phone: '' })
                  }
                }}
                disabled={creatingCustomer}
              >
                <X size={18} />
                Cancelar
              </button>
              <button
                className="customer-modal-btn customer-modal-btn-confirm"
                onClick={handleQuickCreateCustomer}
                disabled={creatingCustomer || !quickCreateForm.name.trim()}
              >
                {creatingCustomer ? 'Cadastrando...' : 'Cadastrar Cliente'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal de Seleção de Tabela de Preços */}
      {priceTableModalOpen && (
        <div className="pre-venda-modal-overlay" onClick={() => setPriceTableModalOpen(false)}>
          <div className="pre-venda-modal-content pre-venda-modal-select" onClick={(e) => e.stopPropagation()}>
            <div className="pre-venda-modal-header">
              <div className="pre-venda-modal-header-left">
                <div className="pre-venda-modal-icon">
                  <Table2 size={24} />
                </div>
                <div>
                  <h3 className="pre-venda-modal-title">Selecionar Tabela de Preços</h3>
                  <p className="pre-venda-modal-subtitle">Tabelas de preços especiais</p>
                  <p className="pre-venda-modal-hint pre-venda-modal-hint--header">
                    <Info size={14} aria-hidden />
                    {PRICE_TABLE_CNPJ_HINT}
                  </p>
                </div>
              </div>
              <button onClick={() => setPriceTableModalOpen(false)} className="pre-venda-modal-close">
                <X size={24} />
              </button>
            </div>

            <div className="pre-venda-modal-body">
              <div className="pre-venda-search-box">
                <Search size={18} className="pre-venda-search-icon" />
                <input
                  type="text"
                  className="pre-venda-search-input"
                  placeholder="Buscar tabela de preços..."
                  value={priceTableSearchTerm}
                  onChange={(e) => setPriceTableSearchTerm(e.target.value)}
                  autoFocus
                />
                {loadingPriceTables && (
                  <div className="pre-venda-search-loading">
                    <Loader2 size={16} className="spinning" />
                  </div>
                )}
              </div>

              <div className="pre-venda-select-list">
                {loadingPriceTables ? (
                  <div className="pre-venda-select-empty">
                    <Loader2 size={32} className="spinning" />
                    <p>Buscando tabelas...</p>
                  </div>
                ) : (
                  <>
                    {/* Opção "Sem tabela" */}
                    <div
                      className={`pre-venda-select-item ${!selectedPriceTable ? 'selected' : ''}`}
                      onClick={() => {
                        setSelectedPriceTable(null)
                        setSelectedPriceTableName(null)
                        setPriceTableModalOpen(false)
                        setPriceTableSearchTerm('')
                      }}
                    >
                      <div className="pre-venda-select-item-avatar">
                        <Table2 size={20} />
                      </div>
                      <div className="pre-venda-select-item-info">
                        <div className="pre-venda-select-item-name">Sem tabela especial</div>
                        <div className="pre-venda-select-item-detail">Usar preços padrão dos produtos</div>
                      </div>
                      {!selectedPriceTable && (
                        <CheckCircle size={20} className="pre-venda-select-item-check" />
                      )}
                    </div>

                    {/* Lista de tabelas */}
                    {priceTables.length > 0 ? (
                      priceTables.map(table => (
                        <div
                          key={table.id}
                          className={`pre-venda-select-item ${Number(selectedPriceTable) === Number(table.id) ? 'selected' : ''}`}
                          onClick={() => {
                            if (!canApplyPriceTable(selectedCustomer)) {
                              showMessage(
                                'Atenção',
                                `Selecione um cliente pessoa jurídica antes de aplicar a tabela de preços. ${PRICE_TABLE_CNPJ_HINT}`,
                                'warning'
                              )
                              return
                            }
                            setSelectedPriceTable(Number(table.id))
                            setSelectedPriceTableName(table.name)
                            setPriceTableModalOpen(false)
                            setPriceTableSearchTerm('')
                          }}
                        >
                          <div className="pre-venda-select-item-avatar">
                            <Table2 size={20} />
                          </div>
                          <div className="pre-venda-select-item-info">
                            <div className="pre-venda-select-item-name">{table.name}</div>
                            {table.code && (
                              <div className="pre-venda-select-item-detail">Código: {table.code}</div>
                            )}
                          </div>
                          {Number(selectedPriceTable) === Number(table.id) && (
                            <CheckCircle size={20} className="pre-venda-select-item-check" />
                          )}
                        </div>
                      ))
                    ) : (
                      <div className="pre-venda-select-empty">
                        <Table2 size={48} />
                        <p>{priceTableSearchTerm.trim() ? 'Nenhuma tabela encontrada' : 'Nenhuma tabela cadastrada'}</p>
                      </div>
                    )}
                  </>
                )}
              </div>
            </div>

            {selectedPriceTable && (
              <div className="pre-venda-modal-footer">
                <button
                  className="pre-venda-modal-btn-secondary"
                  onClick={() => {
                    setSelectedPriceTable(null)
                    setSelectedPriceTableName(null)
                    setPriceTableModalOpen(false)
                    setPriceTableSearchTerm('')
                  }}
                >
                  <X size={18} />
                  Remover Tabela
                </button>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  )
}

// Componente Modal de Quantidade
function ProductQuantityModal({ product, onClose, onConfirm, calculateProductTotal, formatCurrency }) {
  const [newQuantity, setNewQuantity] = useState(product.quantity.toString())
  const calc = calculateProductTotal(product)

  const handleConfirm = () => {
    const qty = parseFloat(newQuantity.replace(',', '.')) || 1
    if (qty < 1) return
    onConfirm(qty)
  }

  return (
    <div className="customer-modal-overlay" onClick={onClose}>
      <div className="customer-modal-content product-action-modal" onClick={(e) => e.stopPropagation()}>
        <div className="customer-modal-header">
          <div className="customer-modal-title-section">
            <h2 className="customer-modal-title">Alteração de quantidade</h2>
          </div>
          <button className="customer-modal-close" onClick={onClose}>
            <X size={20} />
          </button>
        </div>
        <div className="customer-modal-body">
          <div className="product-action-info">
            <h3>{product.name}</h3>
            <div className="product-action-values">
              <div>
                <p className="label">Valor original</p>
                <p>{product.quantity} UN x {formatCurrency(product.unitPrice)} = {formatCurrency(calc.baseTotal)}</p>
              </div>
              <div>
                <p className="label">Valor final</p>
                <p>{product.quantity} UN x {formatCurrency(product.unitPrice)} = {formatCurrency(calc.finalTotal)}</p>
              </div>
            </div>
            <div className="product-action-input-group">
              <label>Nova quantidade</label>
              <div className="quantity-input-wrapper">
                <input
                  type="text"
                  value={newQuantity}
                  onChange={(e) => setNewQuantity(e.target.value)}
                  className="product-action-input"
                />
                <span className="quantity-unit">UN</span>
              </div>
              <p className="stock-info">Quantidade disponível: 0 UN</p>
            </div>
          </div>
        </div>
        <div className="customer-modal-footer">
          <button className="customer-modal-btn customer-modal-btn-remove" onClick={onClose}>
            Cancelar
          </button>
          <button className="customer-modal-btn customer-modal-btn-confirm" onClick={handleConfirm}>
            Lançar quantidade
          </button>
        </div>
      </div>
    </div>
  )
}

// Componente Modal de Desconto
function ProductDiscountModal({ product, onClose, onConfirm, calculateProductTotal, formatCurrency }) {
  const [discountScope, setDiscountScope] = useState('total') // 'unit' ou 'total'
  const [discountType, setDiscountType] = useState('percent') // 'percent' ou 'amount'
  const [discount, setDiscount] = useState(product.discount ? product.discount.toString() : '0')
  
  const baseTotal = product.quantity * product.unitPrice
  const baseUnitPrice = product.unitPrice
  
  // Calcular desconto
  let discountAmount = 0
  if (discountScope === 'unit') {
    // Desconto unitário
    if (discountType === 'percent') {
      const unitDiscount = baseUnitPrice * (parseFloat(discount.replace(',', '.')) || 0) / 100
      discountAmount = unitDiscount * product.quantity
    } else {
      const unitDiscount = parseFloat(discount.replace(',', '.')) || 0
      discountAmount = unitDiscount * product.quantity
    }
  } else {
    // Desconto no total
    if (discountType === 'percent') {
      discountAmount = baseTotal * (parseFloat(discount.replace(',', '.')) || 0) / 100
    } else {
      discountAmount = parseFloat(discount.replace(',', '.')) || 0
    }
  }
  
  const finalTotal = baseTotal - discountAmount
  const finalUnitPrice = baseUnitPrice - (discountAmount / product.quantity)

  const handleConfirm = () => {
    const discountValue = parseFloat(discount.replace(',', '.')) || 0
    // Salvar como 'percent' ou 'amount' baseado no discountType
    onConfirm(discountValue, discountType)
  }

  return (
    <div className="customer-modal-overlay" onClick={onClose}>
      <div className="customer-modal-content product-action-modal" onClick={(e) => e.stopPropagation()}>
        <div className="customer-modal-header">
          <div className="customer-modal-title-section">
            <h2 className="customer-modal-title">Desconto no item</h2>
          </div>
          <button className="customer-modal-close" onClick={onClose}>
            <X size={20} />
          </button>
        </div>
        <div className="customer-modal-body">
          <div className="product-action-info">
            <h3>{product.name}</h3>
            <div className="product-action-values">
              <div>
                <p className="label">Valor original</p>
                <p>{product.quantity} UN x {formatCurrency(product.unitPrice)} = {formatCurrency(baseTotal)}</p>
              </div>
              <div>
                <p className="label">Valor final</p>
                <p>{product.quantity} UN x {formatCurrency(finalUnitPrice)} = {formatCurrency(finalTotal)}</p>
              </div>
            </div>
            <div className="product-action-options">
              <div className="option-buttons">
                <button
                  className={discountScope === 'unit' ? 'active' : ''}
                  onClick={() => setDiscountScope('unit')}
                >
                  Desconto unitário
                </button>
                <button
                  className={discountScope === 'total' ? 'active' : ''}
                  onClick={() => setDiscountScope('total')}
                >
                  Desconto no total
                </button>
              </div>
              <div className="product-action-input-group">
                <label>Desconto (%)</label>
                <input
                  type="text"
                  value={discountType === 'percent' ? discount : ''}
                  onChange={(e) => {
                    setDiscount(e.target.value)
                    setDiscountType('percent')
                  }}
                  className="product-action-input"
                />
              </div>
              <div className="product-action-input-group">
                <label>Desconto (R$)</label>
                <input
                  type="text"
                  value={discountType === 'amount' ? discount : ''}
                  onChange={(e) => {
                    setDiscount(e.target.value)
                    setDiscountType('amount')
                  }}
                  className="product-action-input"
                />
              </div>
              <div className="product-action-summary">
                <p><strong>Valor final:</strong> {formatCurrency(finalTotal)}</p>
              </div>
            </div>
          </div>
        </div>
        <div className="customer-modal-footer">
          <button className="customer-modal-btn customer-modal-btn-remove" onClick={onClose}>
            Cancelar
          </button>
          <button className="customer-modal-btn customer-modal-btn-confirm" onClick={handleConfirm}>
            Lançar desconto
          </button>
        </div>
      </div>
    </div>
  )
}

// Componente Modal de Acréscimo
function ProductSurchargeModal({ product, onClose, onConfirm, calculateProductTotal, formatCurrency }) {
  const [surchargeScope, setSurchargeScope] = useState('total') // 'unit' ou 'total'
  const [surchargeType, setSurchargeType] = useState('percent') // 'percent' ou 'amount'
  const [surcharge, setSurcharge] = useState(product.surcharge ? product.surcharge.toString() : '0')
  
  const baseTotal = product.quantity * product.unitPrice
  const baseUnitPrice = product.unitPrice
  
  // Calcular acréscimo
  let surchargeAmount = 0
  if (surchargeScope === 'unit') {
    // Acréscimo unitário
    if (surchargeType === 'percent') {
      const unitSurcharge = baseUnitPrice * (parseFloat(surcharge.replace(',', '.')) || 0) / 100
      surchargeAmount = unitSurcharge * product.quantity
    } else {
      const unitSurcharge = parseFloat(surcharge.replace(',', '.')) || 0
      surchargeAmount = unitSurcharge * product.quantity
    }
  } else {
    // Acréscimo no total
    if (surchargeType === 'percent') {
      surchargeAmount = baseTotal * (parseFloat(surcharge.replace(',', '.')) || 0) / 100
    } else {
      surchargeAmount = parseFloat(surcharge.replace(',', '.')) || 0
    }
  }
  
  const finalTotal = baseTotal + surchargeAmount
  const finalUnitPrice = baseUnitPrice + (surchargeAmount / product.quantity)

  const handleConfirm = () => {
    let surchargeValue = parseFloat(surcharge.replace(',', '.')) || 0
    
    // Se for acréscimo unitário, converter para total
    if (surchargeScope === 'unit') {
      if (surchargeType === 'percent') {
        // Acréscimo percentual unitário: aplicar a cada unidade e multiplicar pela quantidade
        const unitSurcharge = baseUnitPrice * (surchargeValue / 100)
        surchargeValue = unitSurcharge * product.quantity
        // Salvar como amount (valor total)
        onConfirm(surchargeValue, 'amount')
      } else {
        // Acréscimo em R$ unitário: multiplicar pela quantidade
        surchargeValue = surchargeValue * product.quantity
        onConfirm(surchargeValue, 'amount')
      }
    } else {
      // Acréscimo no total: salvar como está
      onConfirm(surchargeValue, surchargeType)
    }
  }

  return (
    <div className="customer-modal-overlay" onClick={onClose}>
      <div className="customer-modal-content product-action-modal" onClick={(e) => e.stopPropagation()}>
        <div className="customer-modal-header">
          <div className="customer-modal-title-section">
            <h2 className="customer-modal-title">Acréscimo no item</h2>
          </div>
          <button className="customer-modal-close" onClick={onClose}>
            <X size={20} />
          </button>
        </div>
        <div className="customer-modal-body">
          <div className="product-action-info">
            <h3>{product.name}</h3>
            <div className="product-action-values">
              <div>
                <p className="label">Valor original</p>
                <p>{product.quantity} UN x {formatCurrency(product.unitPrice)} = {formatCurrency(baseTotal)}</p>
              </div>
              <div>
                <p className="label">Valor final</p>
                <p>{product.quantity} UN x {formatCurrency(finalUnitPrice)} = {formatCurrency(finalTotal)}</p>
              </div>
            </div>
            <div className="product-action-options">
              <div className="option-buttons">
                <button
                  className={surchargeScope === 'unit' ? 'active' : ''}
                  onClick={() => setSurchargeScope('unit')}
                >
                  Acréscimo unitário
                </button>
                <button
                  className={surchargeScope === 'total' ? 'active' : ''}
                  onClick={() => setSurchargeScope('total')}
                >
                  Acréscimo no total
                </button>
              </div>
              <div className="product-action-input-group">
                <label>Acréscimo (%)</label>
                <input
                  type="text"
                  value={surchargeType === 'percent' ? surcharge : ''}
                  onChange={(e) => {
                    setSurcharge(e.target.value)
                    setSurchargeType('percent')
                  }}
                  className="product-action-input"
                />
              </div>
              <div className="product-action-input-group">
                <label>Acréscimo (R$)</label>
                <input
                  type="text"
                  value={surchargeType === 'amount' ? surcharge : ''}
                  onChange={(e) => {
                    setSurcharge(e.target.value)
                    setSurchargeType('amount')
                  }}
                  className="product-action-input"
                />
              </div>
              <div className="product-action-summary">
                <p><strong>Valor final:</strong> {formatCurrency(finalTotal)}</p>
              </div>
            </div>
          </div>
        </div>
        <div className="customer-modal-footer">
          <button className="customer-modal-btn customer-modal-btn-remove" onClick={onClose}>
            Cancelar
          </button>
          <button className="customer-modal-btn customer-modal-btn-confirm" onClick={handleConfirm}>
            Lançar acréscimo
          </button>
        </div>
      </div>
    </div>
  )
}

export default PreVendaPage
